'use strict';

// external imports
import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';
import moment from 'moment-timezone';
import {unitOfTime} from 'moment';

// prime react imports
import {Card} from 'primereact/card';
import {DataTableStateEvent} from 'primereact/datatable';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import {PropertyValueType} from '~/shared/src/mm/PropertySet';
import ValueSet from '~/shared/src/mm/ValueSet';
import {
    ItemAttributes,
    ItemAttributesForCreation,
    ItemAttributesSettable,
    ItemCollection
} from '~/shared/src/ci/Item';
import {
    ReferenceAttributes,
    ReferenceAttributesForCreation,
    ReferenceCollection
} from '~/shared/src/ci/Reference';
import {
    BaseRequestParameters,
    defaultBaseRequestParameters
} from '~/shared/src/utilities/modules/common';
import {
    getXAxisLabelDescriptor,
    getDataSets,
    LoadColumnDataDescriptorsParameters,
    LoadDataParameters,
    LoadDataResult,
    processDataSet
} from '~/shared/src/utilities/modules/chart';
import {LoadMetaDataRowData} from '~/shared/src/utilities/modules/overview';
import {dataExtraction} from '~/shared/src/utilities/dataExtraction';
import dataUtils from '~/shared/src/utilities/dataUtils';
import {
    buildDataDescriptor,
    DataDescriptor,
    DataStructure,
    extractDataObjectsFromDataStructure,
    StaticDataDescriptor
} from '~/shared/src/utilities/dataStructure';
import {getDataStructureForSqlQuery} from '~/shared/src/utilities/modules/queryLoader';
import {
    ColumnConfig,
    ColumnDefinition as ChartColumnDefinition,
    ColumnFilter,
    ColumnSorter,
    FilterMatchMode,
    FilterValueType
} from '~/shared/src/validationSchemes/modules/overview';
import {Column} from '~/shared/src/utilities/column';
import {fieldTypeToItemTypeMapping} from '~/shared/src/utilities/modules/form';
import {Chart} from '~/shared/src/utilities/chart';
import {ChartDataSet} from '~/shared/src/utilities/chartDataSet';
import {ChartScale} from '~/shared/src/utilities/chartScale';
import {convertValueToString} from '~/shared/src/utilities/valueConverters';

// frontend imports
import ChartComponent, {
    ElusiveData as ChartComponentElusiveData
} from '~/frontend/src/components/base/Chart/index';
import DataTableComponent, {
    DataTable as DataTableComponentClass,
    AllProps as DataTableComponentAllProps,
    ComponentProps as DataTableComponentProps,
    defaultProps as dataTableComponentDefaultProps
} from '~/frontend/src/components/base/DataTable/index';

import {chartUtilities} from '~/frontend/src/components/base/ChartDesigner/utilities';
import {getColumnHeader} from '~/frontend/src/components/base/DataTable/utilities';

// action imports
import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import origSetParams, {
    SetParamsAction
} from '~/frontend/src/actions/panel/common/setParams';
import {
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import addItem from '~/frontend/src/actions/data/addItem';
import updateItem from '~/frontend/src/actions/data/updateItem';
import addReference from '~/frontend/src/actions/data/addReference';
import deleteItem from '~/frontend/src/actions/data/deleteItem';
import deleteReference from '~/frontend/src/actions/data/deleteReference';
import startRequest from '~/frontend/src/actions/ajax/startRequest';
import endRequest from '~/frontend/src/actions/ajax/endRequest';
import loadData from '~/frontend/src/actions/data/loadData';
import loadDataForDataStructure from '~/frontend/src/actions/panel/chartDesigner/loadDataForDataStructure';
import loadDataForQuery from '~/frontend/src/actions/panel/chartDesigner/loadDataForQuery';
import {getValueForDataDescriptorFromRowData} from '~/frontend/src/utilities/dataUtils';
import {getSubPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';


const debugColorScheme = {
    error            : 'F44336',
    loadFunction     : '009688',
    cacheFunction    : '607D8B',
    mouseFunction    : '0095ce',
    dragNdropFunction: 'E91E63',
    helperFunction   : 'FF5722',
    render           : '1565C0'
};
const durationRegExp = /(\+|-) ([0-9]+) (seconds?|minutes?|hours?|days?|months?|years?)$/ig;
const DEFAULT_ROW_COUNTS = {
    TIME_SERIES: 5,
    LINE       : 50,
    DEFAULT    : 10
} as const;


export type ColumnDefinition = ChartColumnDefinition & {
    layoutElementId : number;
};

export type Mode = 'design' | 'input';

export type DragData = {
    items : ItemCollection;
    references? : ReferenceCollection;
};

export type DragOptions = {
    label? : string;
    icon? : string;
};

type InsertPosition = 'before' | 'after' | 'insideBefore' | 'insideAfter';
type DragInfo = {
    dragLayoutElementId : number;
    dragRawColumn : Column;
    rawColumns : Column[];
    rawColumnsOrig : Column[];
    dragElement : HTMLDivElement;
    insertMarkerElement : HTMLDivElement;
    lastDragOverLayoutElementId? : number;
    position? : InsertPosition;
    doNothing : boolean;
};

type NewDragInfo = {
    data : DragData;
    rawColumn? : Column;
};

export type DefaultProps = BaseRequestParameters & {
    /**
     * Additional context to be updated (elusive data)
     */
    additionalElusiveDataContexts? : string[];

    mode? : Mode;
    readOnly? : boolean;
    showData? : boolean;
    showDemoData? : boolean;
    scrollTop? : number;
    scrollLeft? : number;
    tabIndexOffset? : number;
    activeTabContainerIndexes? : {
        [ layoutElementId : number ] : number;
    };
    itemFormStates? : { [ itemId : number ] : 'CLEAR' | 'DIRTY' | 'ERROR' };
};
export const defaultProps : Required<DefaultProps> = {
    ...defaultBaseRequestParameters,

    additionalElusiveDataContexts: [],
    mode                         : 'design',
    readOnly                     : false,
    showData                     : false,
    showDemoData                 : true,
    scrollTop                    : 0,
    scrollLeft                   : 0,
    tabIndexOffset               : 1000,
    activeTabContainerIndexes    : {},
    itemFormStates               : {}
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext for the form data to access global.data with.
     */
    dataContext : string;

    /*** config data ************************************************************************/
    // load from backend

    /**
     * The key of the CFG:Chart item to load the config from.
     */
    viewKey? : string;

    /**
     * A hash of the loaded chart configuration to detect changes.
     */
    configHash? : string;

    // OR provided from outside

    /**
     * The uniqueId of the component which has loaded or builds the config (to track config changes)
     */
    configUid? : string;

    /**
     * The dataContext for the form configuration to access global.data with.
     */
    configDataContext? : string;

    /**
     * The id of the root config item of the form.
     */
    configRootItemId : number;

    /**
     * Additional parameters for the query.
     *
     * NOTE: A mapping between query fields and contents.
     *
     * USAGE:
     * DataStructure based query:
     *   - key: ${<DataObjectOfQuery.itemKey>.<attributePath>}; value: string
     * Cypher or SQL based query:
     *   - key: ${PlaceholderAsWrittenInQuery}; value: string
     */
    additionalParameters? : Required<LoadDataParameters>[ 'parameters' ];


    /*** design mode only *******************************************************************/

    /**
     * The id of currently selected layout Element.
     */
    selectedLayoutElementId? : number;

    /**
     * Callback if a layout element is selected.
     *
     * @param id
     */
    onSelectLayoutElement? : ( id? : number ) => void;

    /**
     * Callback if a layout element displaying data is hovered.
     *
     * @param alias
     */
    onMarkFieldsForAlias? : ( alias : string | string[] | undefined ) => void;


    /*** input mode only ********************************************************************/
};
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

export type AllProps = Required<DefaultProps> & Props;

export type ElusiveData = PanelElusiveDataState & {
    chart? : Chart;
    columnDataDescriptors? : StaticDataDescriptor[];
    columnDefinitions? : ColumnDefinition[];
    columns? : ColumnConfig[];
    dataDescriptors? : DataDescriptor[];
    dataSets? : ChartDataSet[];
    dataStructure? : DataStructure;
    filters? : ColumnFilter[];
    isDataLoaded? : boolean;
    lastLoadParameters? : string;
    lastColumnDataDescriptorParameters? : string;
    rawColumns? : Column[];
    scales? : ChartScale[];
    sorters? : ColumnSorter[];
    xAxisLabelDataDescriptor? : DataDescriptor;
};

export type ReferenceType = {
    chart? : Chart;
    dataStructure? : DataStructure;
    columns? : Column[];
    columnDefinitions? : ColumnDefinition[];
    dataSets? : ChartDataSet[];
    markLayoutElementsForAlias : ( alias : string | undefined ) => void;
    scales? : ChartScale[];
    startDrag : ( rootLayoutElementId : number, data : DragData, options : DragOptions, e : React.DragEvent<HTMLElement> ) => void;
};


const setElusiveData = origSetElusiveData as ( panelKey : string, data : ElusiveData ) => SetElusiveDataAction;
const setElusiveDataForChart = origSetElusiveData as ( panelKey : string, data : ChartComponentElusiveData ) => SetElusiveDataAction;
const setParamsForDataTable = origSetParams as ( panelKey : string, panelProps : Partial<DataTableComponentAllProps> ) => SetParamsAction;

const ChartDesigner = React.forwardRef<ReferenceType, AllProps>( ( props : AllProps, forwardedRef ) => {
    const startTime                                           = performance.now(),
          dispatch : AppDispatch                              = useDispatch(),
          {
              additionalElusiveDataContexts,
              additionalParameters,
              configRootItemId,
              configUid,
              dataContext,
              itemFormStates,
              mode,
              onMarkFieldsForAlias,
              onSelectLayoutElement,
              readOnly,
              selectedLayoutElementId,
              showData,
              showDemoData,
              uid
          }                                                   = props,
          chartUidExtension                                   = 'chart',
          dataTableUidExtension                               = 'dataTable',
          configDataContext                                   = props.configDataContext ?? `${props.dataContext}_metaData`,
          user                                                = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          messageCatalogTimestamp                             = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          metaModelTimestamp                                  = useSelector( ( state : StoreState ) => state.metamodel?.timestamp ? state.metamodel.timestamp : 0 ),
          debugState                                          = useSelector( ( state : StoreState ) => state.debugConfig?.chartDesignerDebug ),
          elusiveData                                         = useSelector( ( state : StoreState ) => state.elusiveData?.[ uid ] as ElusiveData | undefined ),
          elusiveDataConfigData                               = useSelector( ( state : StoreState ) => configUid ? state.elusiveData?.[ configUid ]?.data : state.elusiveData?.[ uid ]?.metaData ),
          chartElusiveData                                    = useSelector( ( state : StoreState ) => state.elusiveData?.[ `${uid}_${chartUidExtension}` ] as ChartComponentElusiveData | undefined ),
          dataTableComponentProps                             = useSelector( ( state : StoreState ) => getSubPanelProps( `${uid}_${dataTableUidExtension}`, state ) as DataTableComponentProps | undefined ),
          dragInfo                                            = useRef<DragInfo | null>( null ),
          newDragInfo                                         = useRef<NewDragInfo | null>( null ),
          additionalRenderTime                                = useRef<number>( 0 ),
          lastParams                                          = useRef<any>( null ),
          hasMounted                                          = useRef<boolean>( elusiveData !== undefined ),
          isLoading                                           = useRef<boolean>( false ),
          isColumnClicked                                     = useRef<boolean>( false ),
          resizingLayoutElementId                             = useRef<number>( 0 ),
          timeoutId                                           = useRef<NodeJS.Timeout | undefined>(),
          dataTable                                           = useRef<DataTableComponentClass>( null ),
          [ dataTableHeight, setDataTableHeight ]             = useState<number>( 30 ),
          [ dataTableHeaderHeight, setDataTableHeaderHeight ] = useState<number>( 30 ),
          [ hoverLayoutElementId, setHoverLayoutElementId ]   = useState<number | undefined>( undefined ),
          [ aliasToMark, setAliasToMark ]                     = useState<string[] | undefined>( undefined ),
          isDataLoaded                                        = useMemo(
              () => {
                  return elusiveData?.isDataLoaded ?? false;
              },
              [
                  elusiveData?.isDataLoaded
              ]
          ),
          globalConfigDataVersions                            = useMemo(
              () => {
                  return JSON.stringify( elusiveDataConfigData ?? {} );
              },
              [
                  elusiveDataConfigData
              ]
          ),
          debug                                               = useMemo(
              () => {
                  return debugState?.showAll ?? debugState?.dedicated?.[ uid ];
              },
              [
                  debugState
              ]
          ),
          chartItem                                           = useMemo(
              () => {
                  return global.data[ configDataContext ]?.items[ configRootItemId ];
              },
              [
                  configDataContext,
                  globalConfigDataVersions,
                  configRootItemId
              ]
          ),
          chart                                               = useMemo(
              () => {
                  return chartItem ? dataExtraction.extractChartData( chartItem, configDataContext ) : undefined;
              },
              [
                  chartItem,
                  configDataContext,
                  globalConfigDataVersions
              ]
          ),
          query                                               = useMemo(
              () => {
                  return chartUtilities.getQuery( configDataContext, configRootItemId );
              },
              [
                  configDataContext,
                  globalConfigDataVersions,
                  configRootItemId
              ]
          ),
          scales                                              = useMemo(
              () => {
                  return chartUtilities.getDataScales( configDataContext, configRootItemId );
              },
              [
                  configDataContext,
                  globalConfigDataVersions,
                  configRootItemId
              ]
          ),
          filters                                             = useMemo(
              () => {
                  let retVal : ColumnFilter[] | undefined;

                  if ( chart?.data.filters ) {
                      try {
                          retVal = JSON.parse( chart.data.filters );
                      }
                      catch ( e ) {
                          console.error( e );
                      }
                  }

                  return retVal;
              },
              [
                  chart
              ]
          ),
          sorters                                             = useMemo(
              () => {
                  let retVal : ColumnSorter[] | undefined;

                  if ( chart?.data.sorters ) {
                      try {
                          retVal = JSON.parse( chart.data.sorters );
                      }
                      catch ( e ) {
                          console.error( e );
                      }
                  }

                  return retVal;
              },
              [
                  chart
              ]
          ),
          settableAdditionalParameters                        = useMemo(
              () => {
                  const retVal : Required<LoadDataParameters>[ 'parameters' ] = {};

                  // filter out special queryFields for timeSeries if, as they are automatically set to generated values
                  if ( additionalParameters && Object.keys( additionalParameters ).length ) {
                      for ( const parameterKey of Object.keys( additionalParameters ) as string[] ) {
                          if (
                              !chart?.data.timeSeriesIntervalUnit
                              || (
                                  parameterKey !== 'queryTimestamp'
                                  && parameterKey !== 'previousQueryTimestamp'
                                  && parameterKey !== ' nextQueryTimestamp'
                              )
                          ) {
                              retVal[ parameterKey ] = additionalParameters[ parameterKey ];
                          }
                      }
                  }

                  return retVal;
              },
              [
                  additionalParameters,
                  chart
              ]
          );

    let dataStructure            = useMemo(
            () => {
                return elusiveData?.dataStructure;
            },
            [
                elusiveData?.dataStructure
            ]
        ),
        xAxisLabelDataDescriptor = useMemo(
            () => {
                return elusiveData?.xAxisLabelDataDescriptor;
            },
            [
                elusiveData?.xAxisLabelDataDescriptor
            ]
        ),
        dataSets                 = useMemo(
            () => {
                return elusiveData?.dataSets;
            },
            [
                elusiveData?.dataSets
            ]
        ),
        dataDescriptors          = useMemo(
            () => {
                return elusiveData?.dataDescriptors;
            },
            [
                elusiveData?.dataDescriptors
            ]
        ),
        columnDefinitions        = useMemo(
            () => {
                return elusiveData?.columnDefinitions;
            },
            [
                elusiveData?.columnDefinitions
            ]
        ),
        rawColumns               = useMemo(
            () => {
                return elusiveData?.rawColumns;
            },
            [
                elusiveData?.rawColumns
            ]
        ),
        columns                  = useMemo(
            () => {
                return elusiveData?.columns;
            },
            [
                elusiveData?.columns
            ]
        );

    const dataSchema                                                 = useMemo(
              () => {
                  return ( dataStructure?.item.objectSchema?.toUpperCase() ?? 'DATA' ) as Schema;
              },
              [
                  dataStructure
              ]
          ),
          isTimeSeriesChart                                          = useMemo(
              () => {
                  return Boolean(
                      query?.type === 'DataStructure'
                      && chart?.data?.timeSeriesIntervalUnit
                      && ( chart.data.timeSeriesIntervalEndAbsolute ?? chart.data.timeSeriesIntervalEndRelative )
                      && ( chart.data.timeSeriesIntervalStartAbsolute ?? chart.data.timeSeriesIntervalStartRelative )
                  );
              },
              [
                  chart,
                  query
              ]
          ),
          getApplyableFilters                                        = ( dataTableFiltersToUse : ColumnFilter[] ) : ColumnFilter[] => {
              const retVal : ColumnFilter[] = [];

              // filter out empty filters
              if ( dataTableFiltersToUse ) {
                  for ( let i = dataTableFiltersToUse.length - 1; i >= 0; i-- ) {
                      if (
                          !retVal.find( filter => filter.field === dataTableFiltersToUse[ i ].field )
                          && ( !!dataTableFiltersToUse[ i ].value || dataTableFiltersToUse[ i ].operator === 'empty' || dataTableFiltersToUse[ i ].operator === 'notEmpty' )
                      ) {
                          retVal.push( {
                              ...dataTableFiltersToUse[ i ]
                          } );
                      }
                  }
              }

              return retVal;
          },
          load                                                       = async ( dataTableFiltersToUse : ColumnFilter[], dataTableSortersToUse : ColumnSorter[] ) => {
              const innerStartTime = debug ? performance.now() : 0;

              let dataLoaded                     = false,
                  chartDataSets : ChartDataSet[] = [];

              if ( chart && chartItem && dataStructure ) {
                  if ( query?.type === 'DataStructure' ) {
                      chartDataSets = dataExtraction.extractChartDataSets( chartItem, configDataContext, dataStructure );
                  }
                  else if ( elusiveData?.columnDataDescriptors ) {
                      chartDataSets = getDataSets( elusiveData.columnDataDescriptors, chartItem, configDataContext, mode === 'design' );
                  }
              }

              try {
                  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
                  if (
                      showData
                      && chart
                      && columnDefinitions
                      && chartDataSets.length
                      && (
                          ( query?.type === 'DataStructure' && dataStructure )
                          || ( query?.type !== 'DataStructure' && query?.query )
                      )
                      && !( settableAdditionalParameters && Object.values( settableAdditionalParameters ).includes( '' ) )
                  ) {
                      /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
                      const loadFilters         = getApplyableFilters( dataTableFiltersToUse ),
                            loadDataDescriptors = columnDefinitions.reduce(
                                ( innerRetVal, cD ) => {
                                    if ( cD.dataDescriptor.field !== 'x' ) {
                                        innerRetVal.push( cD.dataDescriptor );
                                    }

                                    return innerRetVal;
                                },
                                [] as DataDescriptor[]
                            );

                      let params : string = '',
                          result : LoadDataResult | false | undefined;

                      if ( query.type !== 'DataStructure' ) {
                          params = JSON.stringify( {
                              schema         : dataSchema,
                              type           : query.type,
                              query          : query.query,
                              configData     : chart.data,
                              dataDescriptors: loadDataDescriptors,
                              filters        : loadFilters,
                              sorters        : dataTableSortersToUse,
                              parameters     : settableAdditionalParameters
                          } );
                      }
                      else if ( dataStructure ) {
                          params = JSON.stringify( {
                              schema         : dataSchema,
                              dataStructure,
                              configData     : chart.data,
                              xAxisLabelDataDescriptor,
                              dataDescriptors: loadDataDescriptors,
                              filters        : loadFilters,
                              sorters        : dataTableSortersToUse,
                              parameters     : settableAdditionalParameters
                          } );
                      }

                      if ( params !== elusiveData?.lastLoadParameters ) {
                          if ( debug ) {
                              console.groupCollapsed( `%c${'%cChart - %cload%c'.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.loadFunction};`, 'font-weight: normal;' );
                          }

                          isLoading.current = true;

                          dispatch( startRequest(
                              () => {
                              },
                              uid,
                              'processing'
                          ) );

                          if ( query.type !== 'DataStructure' ) {
                              result = await dispatch( loadDataForQuery(
                                  uid,
                                  dataContext,
                                  dataSchema,
                                  'override',
                                  query.type,
                                  query.query,
                                  chart.data,
                                  loadDataDescriptors,
                                  {
                                      filters   : loadFilters,
                                      sorters   : dataTableSortersToUse,
                                      parameters: settableAdditionalParameters
                                  }
                              ) );
                          }
                          else if ( dataStructure ) {
                              result = await dispatch( loadDataForDataStructure(
                                  uid,
                                  dataContext,
                                  dataSchema,
                                  'override',
                                  dataStructure,
                                  chart.data,
                                  xAxisLabelDataDescriptor,
                                  loadDataDescriptors,
                                  {
                                      filters   : loadFilters,
                                      sorters   : dataTableSortersToUse,
                                      parameters: settableAdditionalParameters
                                  }
                              ) );
                          }

                          dispatch( setElusiveData( uid, {
                              lastLoadParameters: params,
                              isDataLoaded      : true
                          } ) );

                          if ( result ) {
                              dataLoaded = true;

                              dispatch( setElusiveDataForChart( `${uid}_${chartUidExtension}`, {
                                  metaData: {
                                      ...chartElusiveData?.metaData,
                                      rowData       : result.metaData.rowData,
                                      rowDataMapping: result.metaData.rowDataMapping
                                  }
                              } ) );
                          }

                          dispatch( endRequest(
                              () => {
                              },
                              uid,
                              'processing'
                          ) );

                          isLoading.current = false;

                          if ( debug ) {
                              console.log( `Chart - %cload end - ${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 106 ), `color: #${debugColorScheme.loadFunction}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
                              console.groupEnd();
                          }
                      }
                  }

                  // generate demo data if
                  if ( !dataLoaded && chart && showDemoData ) {
                      isLoading.current = true;

                      const rowData : LoadMetaDataRowData[] = [],
                            current                         = new Date(),
                            randomValue                     = ( valueType : PropertyValueType, totalRows : number, rowNumber : number ) => {
                                let retVal;

                                switch ( valueType ) {
                                    case 'BOOLEAN':
                                        retVal = Math.random() > .5 ? true : false;
                                        break;

                                    case 'CURRENCY':
                                    case 'DOUBLE':
                                        retVal = Math.random() * 100;
                                        break;

                                    case 'DATE':
                                    case 'TIMESTAMP':
                                        retVal = convertValueToString(
                                            moment( current ).subtract(
                                                    totalRows - ( rowNumber + 1 ),
                                                    'day'
                                                )
                                                .toDate(),
                                            valueType
                                        );
                                        break;

                                    case 'INTEGER':
                                        retVal = Math.round( Math.random() * 100 );
                                        break;

                                    case 'TIME':
                                        retVal = convertValueToString(
                                            moment( current ).subtract(
                                                    ( totalRows - rowNumber - 1 ) * 5,
                                                    'minute'
                                                )
                                                .toDate(),
                                            'TIMESTAMP'
                                        );
                                        break;

                                    default:
                                        retVal = `Label ${rowNumber}`;
                                }

                                return retVal;
                            };

                      let rowCount : number = isTimeSeriesChart
                              ? DEFAULT_ROW_COUNTS.TIME_SERIES
                              : chart.data.type === 'line'
                                  ? DEFAULT_ROW_COUNTS.LINE
                                  : DEFAULT_ROW_COUNTS.DEFAULT,
                          valueSet : ValueSet | undefined;

                      if ( xAxisLabelDataDescriptor ) {
                          const value = getValueForDataDescriptorFromRowData(
                              dataContext,
                              xAxisLabelDataDescriptor,
                              { [ xAxisLabelDataDescriptor.alias ]: 'test' },
                              dataStructure ?? getDataStructureForSqlQuery()
                          );

                          if ( value?.valueSetId ) {
                              valueSet = metaModel[ dataSchema ].getValueSet( value.valueSetId );

                              if ( valueSet?.values ) {
                                  rowCount = valueSet.values.length;
                              }
                              else {
                                  valueSet = undefined;
                              }
                          }
                      }

                      if ( isTimeSeriesChart ) {
                          const momentStartDate = chart.data.timeSeriesIntervalStartAbsolute
                                    ? moment( chart.data.timeSeriesIntervalStartAbsolute )
                                    : moment(),
                                momentEndDate   = chart.data.timeSeriesIntervalEndAbsolute
                                    ? moment( chart.data.timeSeriesIntervalEndAbsolute )
                                    : moment();

                          // relative interval start
                          if (
                              !chart.data.timeSeriesIntervalStartAbsolute
                              && chart.data.timeSeriesIntervalStartRelative?.match( /^now(( (\+|\-) ([0-9]+) (seconds?|minutes?|hours?|days?|months?|years?))*)$/i )
                          ) {
                              let match : RegExpMatchArray | null = durationRegExp.exec( chart.data.timeSeriesIntervalStartRelative ),
                                  count                           = 0;

                              while ( match?.index !== undefined && count++ < 100 ) {
                                  const durationValue = match[ 2 ],
                                        durationUnit  = `${match[ 3 ].toLowerCase()}${!match[ 3 ].match( /s$/i ) ? 's' : ''}` as unitOfTime.DurationConstructor;

                                  if ( match[ 1 ] === '+' ) {
                                      momentStartDate.add( durationValue, durationUnit );
                                  }
                                  else {
                                      momentStartDate.subtract( durationValue, durationUnit );
                                  }

                                  match = durationRegExp.exec( chart.data.timeSeriesIntervalStartRelative );
                              }
                          }

                          // relative interval end
                          if (
                              !chart.data.timeSeriesIntervalEndAbsolute
                              && chart.data.timeSeriesIntervalEndRelative?.match( /^now(( (\+|\-) ([0-9]+) (seconds?|minutes?|hours?|days?|months?|years?))*)$/i )
                          ) {
                              let match : RegExpMatchArray | null = durationRegExp.exec( chart.data.timeSeriesIntervalEndRelative ),
                                  count                           = 0;

                              while ( match?.index !== undefined && count++ < 100 ) {
                                  const durationValue = match[ 2 ],
                                        durationUnit  = `${match[ 3 ].toLowerCase()}${!match[ 3 ].match( /s$/i ) ? 's' : ''}` as unitOfTime.DurationConstructor;

                                  if ( match[ 1 ] === '+' ) {
                                      momentEndDate.add( durationValue, durationUnit );
                                  }
                                  else {
                                      momentEndDate.subtract( durationValue, durationUnit );
                                  }

                                  match = durationRegExp.exec( chart.data.timeSeriesIntervalEndRelative );
                              }
                          }

                          do {
                              const timestamp = momentStartDate.toDate().getTime();

                              for ( let i = 0; i < rowCount; i++ ) {
                                  const row : LoadMetaDataRowData = {};

                                  row.x = timestamp;

                                  if ( xAxisLabelDataDescriptor ) {
                                      if ( valueSet ) {
                                          row[ xAxisLabelDataDescriptor.alias ] = valueSet.values[ i ].value;
                                      }
                                      else {
                                          const valueType = xAxisLabelDataDescriptor.valueType ?? 'STRING';

                                          row[ xAxisLabelDataDescriptor.alias ] = valueType === 'DOUBLE' || valueType === 'INTEGER'
                                              ? i
                                              : randomValue( valueType, rowCount, i );
                                      }
                                  }

                                  if ( dataSets ) {
                                      for ( const dataSet of dataSets ) {
                                          if ( dataSet.dataDescriptor ) {
                                              row[ dataSet.dataDescriptor.alias ] = randomValue( dataSet.dataDescriptor.valueType ?? 'INTEGER', rowCount, i );
                                          }
                                      }
                                  }

                                  rowData.push( row );
                              }

                              // interval step
                              momentStartDate.add( chart.data.timeSeriesIntervalCount ?? 1, chart.data.timeSeriesIntervalUnit );
                          } while ( momentStartDate <= momentEndDate )
                      }
                      else {
                          for ( let i = 0; i < rowCount; i++ ) {
                              const row : LoadMetaDataRowData = {};

                              if ( xAxisLabelDataDescriptor ) {
                                  if ( valueSet ) {
                                      row[ xAxisLabelDataDescriptor.alias ] = valueSet.values[ i ].value;
                                  }
                                  else {
                                      const valueType = xAxisLabelDataDescriptor.valueType ?? 'STRING';

                                      row[ xAxisLabelDataDescriptor.alias ] = valueType === 'DOUBLE' || valueType === 'INTEGER'
                                          ? i
                                          : randomValue( valueType, rowCount, i );
                                  }
                              }

                              if ( dataSets ) {
                                  for ( const dataSet of dataSets ) {
                                      if ( dataSet.dataDescriptor ) {
                                          row[ dataSet.dataDescriptor.alias ] = randomValue( dataSet.dataDescriptor.valueType ?? 'INTEGER', rowCount, i );
                                      }
                                  }
                              }

                              rowData.push( row );
                          }
                      }

                      dispatch( setElusiveData( uid, {
                          lastLoadParameters: undefined,
                          isDataLoaded      : true
                      } ) );

                      dispatch( setElusiveDataForChart( `${uid}_${chartUidExtension}`, {
                          metaData: {
                              ...chartElusiveData?.metaData,
                              rowData
                          }
                      } ) );

                      isLoading.current = false;
                  }
              }
              catch ( e ) {
                  console.error( e );
              }
          },
          showDropZones                                              = () => {
              const chartElement    = document.getElementById( `${uid}_chart` ),
                    chartElementBox = chartElement?.getBoundingClientRect();

              if ( chartElement && chartElementBox ) {
                  // mask delete drop zone
                  let maskElement = document.createElement( 'div' );

                  maskElement.id = `${uid}_chartdesigner_delete_zone`;
                  maskElement.className = 'yn-chartdesigner-delete-zone';
                  maskElement.style.top = `${chartElementBox.y}px`;
                  maskElement.style.width = `${chartElementBox.x}px`;
                  maskElement.style.height = `${chartElementBox.height}px`;
                  document.body.appendChild( maskElement );

                  // mask cancel drop zone
                  maskElement = document.createElement( 'div' );

                  maskElement.id = `${uid}_chartdesigner_cancel_zone`;
                  maskElement.className = 'yn-chartdesigner-cancel-zone';
                  maskElement.style.top = `${chartElementBox.y}px`;
                  maskElement.style.width = `${document.body.clientWidth - chartElementBox.x - chartElementBox.width}px`;
                  maskElement.style.height = `${chartElementBox.height}px`;
                  document.body.appendChild( maskElement );
              }
          },
          hideDropZones                                              = () => {
              const deleteZone = document.getElementById( `${uid}_chartdesigner_delete_zone` ),
                    cancelZone = document.getElementById( `${uid}_chartdesigner_cancel_zone` );

              // unmask delete zone
              if ( deleteZone ) {
                  document.body.removeChild( deleteZone );
              }

              // unmask cancel zone
              if ( cancelZone ) {
                  document.body.removeChild( cancelZone );
              }
          },
          onDragToDelete                                             = () => {
              if ( debug ) {
                  console.groupCollapsed( `%c${`%conDragToDelete%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              const innerStartTime = debug ? performance.now() : 0;

              if ( dragInfo.current ) {
                  showDropZones();

                  // mark drag element to be removed
                  dragInfo.current?.dragElement.classList.add( 'yn-chartdesigner-drag-delete' );

                  if ( newDragInfo.current ) {
                      dragInfo.current.rawColumns = dragInfo.current.rawColumnsOrig;
                      dragInfo.current.doNothing = true;

                      // cleanup
                      dispatch( setElusiveData( uid, {
                          rawColumns: dragInfo.current.rawColumnsOrig
                      } ) );
                  }
                  else if ( dragInfo.current.rawColumns ) {
                      const newRawColumns = JSON.parse( JSON.stringify( dragInfo.current.rawColumns ) ) as Column[],
                            dragRawColumn = dragInfo.current.dragRawColumn;

                      let index = newRawColumns.findIndex( col => col.data.id === dragRawColumn.data.id );

                      // remove from rawColumns
                      if ( index !== -1 ) {
                          newRawColumns.splice( index, 1 );
                      }

                      // reorder columns in rawColumns
                      for ( index = 0; index < newRawColumns.length; index++ ) {
                          newRawColumns[ index ].data.order = 10 * ( index + 1 );
                      }

                      // update dragInfo
                      dragInfo.current.rawColumns = newRawColumns;
                      dragInfo.current.doNothing = false;

                      // update elusiveData
                      dispatch( setElusiveData( uid, {
                          rawColumns: newRawColumns
                      } ) );
                  }
              }

              if ( debug ) {
                  console.log( `%c${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: unset;' );
                  console.groupEnd();
              }
          },
          onDragOver                                                 = ( layoutElementId : number, position : InsertPosition ) => {
              if ( debug ) {
                  console.groupCollapsed( `%c${`%conDragOver%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              const innerStartTime = debug ? performance.now() : 0;

              if ( dragInfo.current && ( dragInfo.current?.rawColumns || layoutElementId === chart?.data.id ) ) {
                  const newRawColumns = (
                            dragInfo.current?.rawColumns
                                ? JSON.parse( JSON.stringify( dragInfo.current.rawColumns ) )
                                : []
                        ) as Column[],
                        dragRawColumn = dragInfo.current.dragRawColumn;

                  let index = newRawColumns.findIndex( col => col.data.id === dragRawColumn.data.id );

                  // remove from rawColumns, but not timestamp or label column
                  if ( index > ( isTimeSeriesChart ? 1 : 0 ) ) {
                      newRawColumns.splice( index, 1 );
                  }

                  // insert into rawColumns
                  index = newRawColumns.findIndex( col => col.data.id === layoutElementId );

                  if ( index !== -1 || !newRawColumns.length ) {

                      // always after label column
                      if ( index <= ( isTimeSeriesChart ? 1 : 0 ) ) {
                          index = ( isTimeSeriesChart ? 1 : 0 );
                          position = 'after';
                      }

                      if ( !newRawColumns.length ) {
                          newRawColumns.push( dragRawColumn );
                      }
                      else if ( position === 'before' ) {
                          newRawColumns.splice( index, 0, dragRawColumn );
                      }
                      else {
                          newRawColumns.splice( index + 1, 0, dragRawColumn );
                      }

                      // reorder columns in rawColumns
                      for ( index = 0; index < newRawColumns.length; index++ ) {
                          newRawColumns[ index ].data.order = 10 * ( index + 1 );
                      }

                      // mark drag element ok
                      dragInfo.current?.dragElement.classList.add( 'yn-chartdesigner-drag-ok' );

                      // update dragInfo
                      dragInfo.current.rawColumns = newRawColumns;
                      dragInfo.current.doNothing = false;

                      // update elusiveData
                      dispatch( setElusiveData( uid, {
                          rawColumns: newRawColumns
                      } ) );
                  }
              }

              if ( debug ) {
                  console.log( `%c${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: unset;' );
                  console.groupEnd();
              }
          },
          onDragOut                                                  = () => {
              if ( debug ) {
                  console.log( `%c${`%conDragOut%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              if ( dragInfo.current ) {
                  showDropZones();

                  // mark drag element to not changed
                  dragInfo.current.dragElement.classList.add( 'yn-chartdesigner-drag-nothing' );

                  // update dragInfo
                  dragInfo.current.rawColumns = dragInfo.current.rawColumnsOrig;
                  dragInfo.current.doNothing = true;

                  // cleanup
                  dispatch( setElusiveData( uid, {
                      rawColumns: dragInfo.current.rawColumnsOrig
                  } ) );
              }
          },
          onDrop                                                     = () => {
              if ( debug ) {
                  console.groupCollapsed( `%c${`%conDrop%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              const innerStartTime  = debug ? performance.now() : 0,
                    rawColumnsToUse = ( [] as Column[] ).concat( lastParams.current?.rawColumns );

              if ( dragInfo.current && !dragInfo.current.doNothing ) {
                  const propertyTypeIdString = String( metaModel[ 'CFG' ].getPropertyType( 'Order' )?.id ?? 0 );

                  // add given drag data
                  if ( newDragInfo.current ) {
                      if ( debug ) {
                          console.log( '-> adding new data' );
                      }

                      const oldToNewItemIdMapping : {
                          [ oldId : string ] : string
                      } = {};

                      for ( const item of Object.values( newDragInfo.current.data.items ) ) {
                          const itemAttributes = item.toJSON( 'all' ) as ItemAttributes;

                          // delete id
                          if ( item.id < 0 ) {
                              delete itemAttributes.id;
                          }
                          // delete new ids for create properties
                          for ( const property of itemAttributes.properties ?? [] ) {
                              if ( ( property.id ?? 0 ) < 0 ) {
                                  delete property.id;
                              }
                          }
                          // delete new ids for create enumProperties
                          for ( const enumProperty of itemAttributes.enumProperties ?? [] ) {
                              for ( const property of enumProperty.properties ?? [] ) {
                                  if ( ( property.id ?? 0 ) < 0 ) {
                                      delete property.id;
                                  }
                              }
                          }

                          if ( debug ) {
                              console.log( '    -> adding new item', itemAttributes );
                          }

                          const newItem = dispatch( addItem(
                              configDataContext,
                              itemAttributes as ItemAttributesForCreation,
                              'CFG',
                              additionalElusiveDataContexts
                          ) );

                          if ( newItem ) {
                              // update dragItem with newItem
                              if ( item.id === dragInfo.current.dragRawColumn.data.id ) {
                                  const column = dragInfo.current.rawColumns.find( col => col.data.id === dragInfo.current!.dragRawColumn.data.id );

                                  if ( column ) {
                                      column.data.id = newItem.id;
                                  }
                              }

                              if ( item.id < 0 ) {
                                  oldToNewItemIdMapping[ String( item.id ) ] = String( newItem.id );
                              }
                          }
                      }
                      if ( newDragInfo.current.data.references ) {
                          for ( const reference of Object.values( newDragInfo.current.data.references ) ) {
                              const referenceAttributes = reference.toJSON() as ReferenceAttributes;

                              // remap item ids
                              if ( oldToNewItemIdMapping[ String( reference.srcItemId ) ] ) {
                                  referenceAttributes.srcItemId = Number( oldToNewItemIdMapping[ String( reference.srcItemId ) ] );
                              }
                              if ( oldToNewItemIdMapping[ String( reference.destItemId ) ] ) {
                                  referenceAttributes.destItemId = Number( oldToNewItemIdMapping[ String( reference.destItemId ) ] );
                              }

                              // delete new ids for create reference
                              if ( reference.id < 0 ) {
                                  delete referenceAttributes.id;
                              }
                              // delete new ids for create referenceProperties
                              for ( const property of referenceAttributes.properties ?? [] ) {
                                  if ( ( property.id ?? 0 ) < 0 ) {
                                      delete property.id;
                                  }
                              }
                              // delete new ids for create enumReferenceProperties
                              for ( const enumProperty of referenceAttributes.enumProperties ?? [] ) {
                                  for ( const property of enumProperty.properties ?? [] ) {
                                      if ( ( property.id ?? 0 ) < 0 ) {
                                          delete property.id;
                                      }
                                  }
                              }

                              if ( debug ) {
                                  console.log( '    -> adding new reference', referenceAttributes );
                              }

                              dispatch( addReference(
                                  configDataContext,
                                  referenceAttributes as ReferenceAttributesForCreation,
                                  'CFG',
                                  additionalElusiveDataContexts
                              ) );
                          }
                      }

                      // select dropped item
                      lastParams.current?.onSelectLayoutElement?.( dragInfo.current.dragRawColumn.data.id );
                  }

                  // remove
                  else if ( !dragInfo.current.lastDragOverLayoutElementId ) {
                      if ( debug ) {
                          console.log( '-> processing delete' );
                      }

                      const oldItem = global.data[ configDataContext ].items[ dragInfo.current.dragLayoutElementId ];

                      if ( oldItem?.itemType.name === 'ChartDataSet' ) {
                          if ( debug ) {
                              console.log( '    -> deleting item', oldItem.toJSON() );
                          }

                          const index = rawColumnsToUse.findIndex( rC => rC.data.id === dragInfo.current!.dragLayoutElementId );

                          dispatch( deleteItem(
                              configDataContext,
                              oldItem.id,
                              undefined,
                              additionalElusiveDataContexts
                          ) );

                          // delete all other references
                          for ( const referenceId of oldItem.references ) {
                              dispatch( deleteReference(
                                  configDataContext,
                                  referenceId,
                                  undefined,
                                  additionalElusiveDataContexts
                              ) );
                          }

                          // remove from rawColumns
                          if ( index !== -1 ) {
                              rawColumnsToUse.splice( index, 1 );
                          }

                          lastParams.current?.onSelectLayoutElement?.( configRootItemId );
                      }
                  }

                  // set orders
                  if ( debug ) {
                      console.log( '    -> reordering layout elements in parent' );
                  }

                  for ( let index = 0; index < rawColumnsToUse.length; index++ ) {
                      const columnItem = global.data[ configDataContext ].items[ rawColumnsToUse[ index ].data.id ];

                      if ( columnItem?.itemType.name === 'ChartDataSet' ) {
                          const data : ItemAttributesSettable = {
                              properties: {
                                  [ propertyTypeIdString ]: {
                                      value: 10 * ( index + 1 )
                                  }
                              }
                          };

                          dispatch( updateItem(
                              configDataContext,
                              columnItem.id,
                              data,
                              additionalElusiveDataContexts
                          ) );
                      }
                  }

                  const {
                            xAxisLabelDataDescriptor: newXAxisLabelDataDescriptor,
                            dataSets                : newDataSets,
                            dataDescriptors         : newDataDescriptors
                        } = buildXAxisLabelDataDescriptorAndDataSetsAndDataDescriptors( dataStructure, elusiveData?.columnDataDescriptors );

                  dispatch( setElusiveData( uid, {
                      xAxisLabelDataDescriptor: newXAxisLabelDataDescriptor,
                      dataSets                : newDataSets,
                      dataDescriptors         : newDataDescriptors
                  } ) );
              }

              if ( debug ) {
                  console.log( `%c${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: unset;' );
                  console.groupEnd();
              }
          },
          onMouseMove                                                = ( e : MouseEvent ) : void => {
              if ( debug ) {
                  console.groupCollapsed( `%c${`%conMouseMove%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              const innerStartTime = debug ? performance.now() : 0;

              let layoutElementId : number | undefined,
                  position : InsertPosition | undefined;

              if ( dragInfo.current ) {
                  // set position of drag ghost
                  dragInfo.current.dragElement.style.left = ( e.pageX - 50 ) + 'px';
                  dragInfo.current.dragElement.style.top = ( e.pageY - 50 ) + 'px';

                  // get drag over layoutElement
                  if ( dragInfo.current.rawColumns ) {
                      const chartElement    = document.getElementById( `${uid}_chart` ),
                            chartElementBox = chartElement?.getBoundingClientRect(),
                            cleanMarker     = () => {
                                hideDropZones();

                                // unmark drag element
                                dragInfo.current?.dragElement.classList.remove( 'yn-chartdesigner-drag-ok', 'yn-chartdesigner-drag-nothing', 'yn-chartdesigner-drag-delete' );
                            };

                      // get column under pointer
                      for ( const rawColumn of dragInfo.current.rawColumns ) {
                          const element = document.getElementById( `leid_${rawColumn.data.id}` );

                          if ( element ) {
                              const elementBox = element.getBoundingClientRect();

                              if (
                                  e.pageX >= elementBox.x && e.pageX <= ( elementBox.x + elementBox.width )
                                  && e.pageY >= elementBox.y && e.pageY <= ( elementBox.y + dataTableHeight )
                              ) {
                                  layoutElementId = rawColumn.data.id;

                                  position = 'after';
                                  if ( e.pageX <= ( elementBox.x + elementBox.width / 2 ) ) {
                                      position = 'before';
                                  }

                                  break;
                              }
                          }
                      }

                      // no layoutElementId and pointer within chart -> set to chart layoutElementId
                      if ( !layoutElementId ) {
                          if (
                              chartElementBox
                              && e.pageX > chartElementBox.x && e.pageX < ( chartElementBox.x + chartElementBox.width )
                              && e.pageY > chartElementBox.y && e.pageY < ( chartElementBox.y + chartElementBox.height )
                          ) {
                              layoutElementId = configRootItemId;

                              position = 'insideAfter';
                              if ( e.pageX <= ( chartElementBox.x + chartElementBox.width / 2 ) ) {
                                  position = 'insideBefore';
                              }
                          }
                      }

                      if ( debug ) {
                          console.log( '    layoutElementId', layoutElementId );
                          console.log( '    position', position );
                      }

                      // drag over layoutElement found
                      // and position
                      // and dragged layoutElement not the drag over layoutElement
                      // and drop target layoutElement or position changed
                      if (
                          layoutElementId
                          && position
                          && dragInfo.current.dragLayoutElementId !== layoutElementId
                          && ( layoutElementId !== dragInfo.current.lastDragOverLayoutElementId || position !== dragInfo.current.position )
                      ) {
                          // mark drag over element (hover)
                          if ( lastParams.current?.hoverLayoutElementId !== layoutElementId ) {
                              if ( debug ) {
                                  console.log( '    -> set hoverLayoutElementId' );
                              }

                              setHoverLayoutElementId( layoutElementId );
                          }

                          dragInfo.current.lastDragOverLayoutElementId = layoutElementId;
                          dragInfo.current.position = position;

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }
                          timeoutId.current = setTimeout(
                              () => {
                                  clearTimeout( timeoutId.current );

                                  cleanMarker();

                                  // handle drag over
                                  onDragOver( layoutElementId!, position! );
                              },
                              100
                          );
                      }
                      else if ( !layoutElementId ) {
                          delete dragInfo.current.lastDragOverLayoutElementId;
                          delete dragInfo.current.position;

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }

                          // dragged to drag out (delete) zone
                          if (
                              chartElementBox
                              && e.pageX < chartElementBox.x
                              && e.pageY > chartElementBox.y
                          ) {
                              timeoutId.current = setTimeout(
                                  () => {
                                      clearTimeout( timeoutId.current );

                                      cleanMarker();

                                      // handle delete
                                      onDragToDelete();
                                  },
                                  100
                              );
                          }
                          else {
                              timeoutId.current = setTimeout(
                                  () => {
                                      clearTimeout( timeoutId.current );

                                      cleanMarker();

                                      // handle drag out
                                      onDragOut();
                                  },
                                  100
                              );
                          }
                      }
                  }
              }

              if ( debug ) {
                  console.log( `%c${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: unset;' );
                  console.groupEnd();
              }
          },
          onMouseUp                                                  = ( e : MouseEvent | React.MouseEvent<HTMLDivElement> ) : void => {
              if ( debug ) {
                  console.log( `%c${`%conMouseUp%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              // remove listeners for drag control
              document.querySelector( 'html' )?.removeEventListener( 'mouseup', onMouseUp );
              document.querySelector( 'html' )?.removeEventListener( 'mousemove', onMouseMove );

              // destroy drag ghost
              if ( dragInfo.current?.dragElement ) {
                  document.body.removeChild( dragInfo.current.dragElement );
              }

              hideDropZones();

              onDrop();

              // clean drag info
              dragInfo.current = null;
              // clean new drag info
              newDragInfo.current = null;

              setHoverLayoutElementId( undefined );
              lastParams.current.onMarkFieldsForAlias?.( undefined );
          },
          onDragStart                                                = ( layoutElementId : number, e : React.DragEvent<HTMLElement>, options? : DragOptions ) : void => {
              const innerStartTime     = debug ? performance.now() : 0,
                    rawColumnsToUse    = lastParams.current?.rawColumns as Column[] | undefined,
                    dataStructureToUse = lastParams.current?.dataStructure as DataStructure | undefined,
                    columnDefinitionsToUse
                                       = lastParams.current?.columnDefinitions as ColumnDefinition[] | undefined;

              if ( !resizingLayoutElementId.current && rawColumnsToUse ) {
                  const rawColumn = newDragInfo.current?.rawColumn ?? rawColumnsToUse.find( ( col : Column ) => col.data.id === layoutElementId );

                  if ( rawColumn ) {
                      const columnDefinition = columnDefinitionsToUse?.find( colDef => colDef.field === rawColumn.dataDescriptor?.alias );

                      if ( !columnDefinition || !columnDefinition.hidden ) {
                          if ( debug ) {
                              console.groupCollapsed( `%c${`%conDragStart - ${rawColumn?.data?.key ?? layoutElementId}%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: normal;' );
                          }

                          // init drag info
                          dragInfo.current = {
                              dragLayoutElementId: layoutElementId,
                              dragRawColumn      : rawColumn,
                              rawColumns         : rawColumnsToUse,
                              rawColumnsOrig     : rawColumnsToUse,
                              dragElement        : document.createElement( 'div' ),
                              insertMarkerElement: document.createElement( 'div' ),
                              doNothing          : true
                          };

                          if ( newDragInfo.current ) {
                              // use only for init drag info
                              delete newDragInfo.current.rawColumn;
                          }

                          // create drag ghost
                          const fieldPreview       = document.createElement( 'div' ),
                                layoutElementLabel = options?.label
                                    ?? ( columnDefinition && dataStructureToUse
                                            ? getColumnHeader( columnDefinition, dataStructureToUse, ( rawColumn.dataDescriptor?.dataObject?.item.objectSchema ?? 'DATA' ) as Schema, false, false )
                                            : 'Column'
                                    ),
                                valueType          = rawColumn.dataDescriptor
                                    ? dataUtils.getDataValueType( rawColumn.dataDescriptor, 'CFG', dataStructure )
                                    : undefined,
                                layoutElementIcon  = options?.icon ?? `yn-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( valueType ? fieldTypeToItemTypeMapping[ valueType as PropertyValueType ] : '' )?.icon}`;

                          dragInfo.current.dragElement.id = 'drag';
                          dragInfo.current.dragElement.className = 'yn-chartdesigner-drag';
                          dragInfo.current.dragElement.style.left = ( e.pageX - 50 ) + 'px';
                          dragInfo.current.dragElement.style.top = ( e.pageY - 50 ) + 'px';
                          document.body.appendChild( dragInfo.current.dragElement );

                          fieldPreview.id = 'drag_preview';
                          fieldPreview.className = 'yn-chartdesigner-drag-preview p-component yn-fieldlike';
                          fieldPreview.innerHTML = `<span class='${layoutElementIcon}'></span>${layoutElementLabel}`;
                          dragInfo.current.dragElement.appendChild( fieldPreview );

                          // add listeners for drag control
                          document.querySelector( 'html' )?.addEventListener( 'mousemove', onMouseMove );
                          document.querySelector( 'html' )?.addEventListener( 'mouseup', onMouseUp );

                          if ( debug ) {
                              console.log( `%c${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.dragNdropFunction}; font-weight: bold;`, 'font-weight: unset;' );
                              console.groupEnd();
                          }
                      }
                  }
              }

              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.preventDefault();
          },
          onMouseEnter                                               = ( layoutElementId : number, e : React.MouseEvent<HTMLDivElement> ) : void => {
              const rawColumnsToUse = lastParams.current?.rawColumns as Column[],
                    rawColumn       = rawColumnsToUse?.find( ( col : Column ) => col.data.id === layoutElementId );

              if ( debug ) {
                  console.groupCollapsed( `%c${`%conMouseEnter - ${rawColumn?.data.key ?? layoutElementId}%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.mouseFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              e.stopPropagation();

              if ( !resizingLayoutElementId.current && lastParams.current?.hoverLayoutElementId !== layoutElementId ) {
                  if ( debug ) {
                      console.log( '-> set hoverLayoutElementId', layoutElementId );
                  }

                  setHoverLayoutElementId( layoutElementId );
                  lastParams.current?.onMarkFieldsForAlias?.( rawColumn?.dataDescriptor?.fullAlias.split( ',' ) );
              }

              if ( debug ) {
                  console.groupEnd();
              }
          },
          onMouseLeave                                               = ( layoutElementId : number, e : React.MouseEvent<HTMLDivElement> ) : void => {
              const rawColumnsToUse = lastParams.current?.rawColumns,
                    rawColumn       = rawColumnsToUse.find( ( col : Column ) => col.data.id === layoutElementId );

              if ( debug ) {
                  console.groupCollapsed( `%c${`%conMouseLeave - ${rawColumn?.data.key ?? layoutElementId}%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.mouseFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              if ( !resizingLayoutElementId.current && lastParams.current?.hoverLayoutElementId === layoutElementId ) {
                  setHoverLayoutElementId( undefined );
                  lastParams.current?.onMarkFieldsForAlias?.( undefined );
              }

              if ( debug ) {
                  console.groupEnd();
              }
          },
          onClick                                                    = ( layoutElementId : number | undefined, e : React.MouseEvent<HTMLDivElement> ) : void => {
              const element        = document.getElementById( `leid_${layoutElementId}` ),
                    resizerElement = element?.querySelector( '.p-column-resizer' ),
                    elementBox     = element?.getBoundingClientRect();

              if ( debug ) {
                  const rawColumnsToUse = lastParams.current?.rawColumns,
                        rawColumn       = rawColumnsToUse.find( ( col : Column ) => col.data.id === layoutElementId );

                  console.log( `%c${`%conClick - ${rawColumn?.data.key ?? layoutElementId}%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.mouseFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              if ( ( e.target === element && ( !elementBox || e.pageY > ( elementBox.y + dataTableHeaderHeight ) ) ) || e.target === resizerElement ) {
                  const resizerElementBox  = resizerElement?.getBoundingClientRect(),
                        deleteElementStyle = resizerElement
                            ? window.getComputedStyle( resizerElement, ':after' )
                            : undefined,
                        deleteElementBox   = resizerElementBox && deleteElementStyle
                            ? {
                                x     : resizerElementBox.x + parseFloat( deleteElementStyle.left ),
                                width : parseFloat( deleteElementStyle.width ) + parseFloat( deleteElementStyle.paddingLeft ) + parseFloat( deleteElementStyle.paddingRight ) + parseFloat( deleteElementStyle.marginLeft ) + parseFloat( deleteElementStyle.marginRight ),
                                y     : resizerElementBox.y + parseFloat( deleteElementStyle.top ),
                                height: parseFloat( deleteElementStyle.height ) + parseFloat( deleteElementStyle.paddingTop ) + parseFloat( deleteElementStyle.paddingBottom ) + parseFloat( deleteElementStyle.marginTop ) + parseFloat( deleteElementStyle.marginBottom )
                            }
                            : undefined;

                  // check if selected and delete is clicked
                  if (
                      layoutElementId === lastParams.current?.selectedLayoutElementId
                      && deleteElementBox
                      && e.pageX > deleteElementBox.x
                      && e.pageX < ( deleteElementBox.x + deleteElementBox.width )
                      && e.pageY > deleteElementBox.y
                      && e.pageY < ( deleteElementBox.y + deleteElementBox.height )
                  ) {
                      const rawColumnsToUse      = ( [] as Column[] ).concat( lastParams.current?.rawColumns ),
                            propertyTypeIdString = String( metaModel[ 'CFG' ].getPropertyType( 'Order' )?.id ?? 0 ),
                            oldItem              = global.data[ configDataContext ].items[ layoutElementId ?? 0 ];

                      if ( debug ) {
                          console.log( '-> processing delete' );
                      }

                      if ( oldItem?.itemType.name === 'ChartDataSet' ) {
                          if ( debug ) {
                              console.log( '    -> deleting item', oldItem.toJSON() );
                          }

                          let index = rawColumnsToUse.findIndex( col => col.data.id === layoutElementId );

                          dispatch( deleteItem(
                              configDataContext,
                              oldItem.id,
                              undefined,
                              additionalElusiveDataContexts
                          ) );

                          // delete all other references
                          for ( const referenceId of oldItem.references ) {
                              dispatch( deleteReference(
                                  configDataContext,
                                  referenceId,
                                  undefined,
                                  additionalElusiveDataContexts
                              ) );
                          }

                          // remove from rawColumns
                          if ( index !== -1 ) {
                              rawColumnsToUse.splice( index, 1 );
                          }

                          // set orders
                          for ( index = 0; index < rawColumnsToUse.length; index++ ) {
                              const columnItem = global.data[ configDataContext ].items[ rawColumnsToUse[ index ].data.id ];

                              if ( columnItem?.itemType.name === 'ChartDataSet' ) {
                                  const data : ItemAttributesSettable = {
                                      properties: {
                                          [ propertyTypeIdString ]: {
                                              value: 10 * ( index + 1 )
                                          }
                                      }
                                  };

                                  dispatch( updateItem(
                                      configDataContext,
                                      columnItem.id,
                                      data,
                                      additionalElusiveDataContexts
                                  ) );
                              }
                          }

                          lastParams.current?.onSelectLayoutElement?.( configRootItemId );

                          const {
                                    xAxisLabelDataDescriptor: newXAxisLabelDataDescriptor,
                                    dataSets                : newDataSets,
                                    dataDescriptors         : newDataDescriptors
                                } = buildXAxisLabelDataDescriptorAndDataSetsAndDataDescriptors( dataStructure, elusiveData?.columnDataDescriptors );

                          dispatch( setElusiveData( uid, {
                              xAxisLabelDataDescriptor: newXAxisLabelDataDescriptor,
                              dataSets                : newDataSets,
                              dataDescriptors         : newDataDescriptors
                          } ) );
                      }
                  }

                  else {
                      lastParams.current?.onSelectLayoutElement?.( layoutElementId );
                  }

                  e.preventDefault();
                  e.stopPropagation();
              }
              else {
                  isColumnClicked.current = true;
              }
          },
          onColumnResizeStart                                        = ( layoutElementId : number, e : React.MouseEvent<HTMLDivElement> ) => {
              if ( debug ) {
                  const rawColumnsToUse = lastParams.current?.rawColumns,
                        rawColumn       = rawColumnsToUse.find( ( col : Column ) => col.data.id === layoutElementId );

                  console.log( `%c${`%conColumnResizeStart - ${rawColumn?.data.key ?? layoutElementId}%c`.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.mouseFunction}; font-weight: bold;`, 'font-weight: normal;' );
              }

              const target             = e.target as HTMLElement,
                    targetBoundingBox  = target.getBoundingClientRect(),
                    deleteElementStyle = window.getComputedStyle( target, ':after' ),
                    deleteElementBox   = targetBoundingBox && deleteElementStyle
                        ? {
                            x     : targetBoundingBox.x + parseFloat( deleteElementStyle.left ),
                            width : parseFloat( deleteElementStyle.width ) + parseFloat( deleteElementStyle.paddingLeft ) + parseFloat( deleteElementStyle.paddingRight ) + parseFloat( deleteElementStyle.marginLeft ) + parseFloat( deleteElementStyle.marginRight ),
                            y     : targetBoundingBox.y + parseFloat( deleteElementStyle.top ),
                            height: parseFloat( deleteElementStyle.height ) + parseFloat( deleteElementStyle.paddingTop ) + parseFloat( deleteElementStyle.paddingBottom ) + parseFloat( deleteElementStyle.marginTop ) + parseFloat( deleteElementStyle.marginBottom )
                        }
                        : undefined;

              // check if selected and delete is clicked
              if (
                  layoutElementId === lastParams.current?.selectedLayoutElementId
                  && deleteElementBox
                  && e.pageX > deleteElementBox.x
                  && e.pageX < ( deleteElementBox.x + deleteElementBox.width )
                  && e.pageY > deleteElementBox.y
                  && e.pageY < ( deleteElementBox.y + deleteElementBox.height )
              ) {
                  const rawColumnsToUse      = ( [] as Column[] ).concat( lastParams.current?.rawColumns ),
                        propertyTypeIdString = String( metaModel[ 'CFG' ].getPropertyType( 'Order' )?.id ?? 0 ),
                        oldItem              = global.data[ configDataContext ].items[ layoutElementId ];

                  if ( debug ) {
                      console.log( '-> processing delete' );
                  }

                  if ( oldItem?.itemType.name === 'ChartDataSet' ) {
                      if ( debug ) {
                          console.log( '    -> deleting item', oldItem.toJSON() );
                      }

                      let index = rawColumnsToUse.findIndex( col => col.data.id === layoutElementId );

                      dispatch( deleteItem(
                          configDataContext,
                          oldItem.id,
                          undefined,
                          additionalElusiveDataContexts
                      ) );

                      // delete all other references
                      for ( const referenceId of oldItem.references ) {
                          dispatch( deleteReference(
                              configDataContext,
                              referenceId,
                              undefined,
                              additionalElusiveDataContexts
                          ) );
                      }

                      // remove from rawColumns
                      if ( index !== -1 ) {
                          rawColumnsToUse.splice( index, 1 );
                      }

                      // set orders
                      for ( index = 0; index < rawColumnsToUse.length; index++ ) {
                          const columnItem = global.data[ configDataContext ].items[ rawColumnsToUse[ index ].data.id ];

                          if ( columnItem?.itemType.name === 'ChartDataSet' ) {
                              const data : ItemAttributesSettable = {
                                  properties: {
                                      [ propertyTypeIdString ]: {
                                          value: 10 * ( index + 1 )
                                      }
                                  }
                              };

                              dispatch( updateItem(
                                  configDataContext,
                                  columnItem.id,
                                  data,
                                  additionalElusiveDataContexts
                              ) );
                          }
                      }

                      lastParams.current?.onSelectLayoutElement?.( configRootItemId );

                      const {
                                xAxisLabelDataDescriptor: newXAxisLabelDataDescriptor,
                                dataSets                : newDataSets,
                                dataDescriptors         : newDataDescriptors
                            } = buildXAxisLabelDataDescriptorAndDataSetsAndDataDescriptors( dataStructure, elusiveData?.columnDataDescriptors );

                      dispatch( setElusiveData( uid, {
                          xAxisLabelDataDescriptor: newXAxisLabelDataDescriptor,
                          dataSets                : newDataSets,
                          dataDescriptors         : newDataDescriptors
                      } ) );
                  }
              }
              else {
                  resizingLayoutElementId.current = layoutElementId;
              }

              e.stopPropagation();
              e.nativeEvent.stopPropagation();
          },
          onChartClick                                               = () => {
              if ( !resizingLayoutElementId.current && !isColumnClicked.current ) {
                  lastParams.current?.onSelectLayoutElement?.( configRootItemId );
              }
              else {
                  resizingLayoutElementId.current = 0;
                  isColumnClicked.current = false;
              }
          },
          onChartLeave                                               = ( e : React.MouseEvent<HTMLDivElement> ) : void => {
              setHoverLayoutElementId( undefined );
              lastParams.current?.onMarkFieldsForAlias?.( undefined );
          },
          onSort                                                     = useCallback(
              ( event : DataTableStateEvent ) => {
                  if ( event.multiSortMeta ) {
                      const data : ItemAttributesSettable = {},
                            sortersPropertyTypeIdString   = String( metaModel[ 'CFG' ].getPropertyType( 'Sorters' )!.id ),
                            newSorters : ColumnSorter[]   = [];

                      for ( let index = 0; index < event.multiSortMeta.length; index++ ) {
                          newSorters.push( {
                              key      : event.multiSortMeta[ index ].field,
                              field    : event.multiSortMeta[ index ].field,
                              direction: event.multiSortMeta[ index ].order === 1 ? 'ASC' : 'DESC'
                          } );
                      }

                      if ( newSorters.length ) {
                          data.properties = {
                              [ sortersPropertyTypeIdString ]: {
                                  statusId: metaModel[ 'CFG' ].getStatus( 'active' )?.id,
                                  value   : JSON.stringify( newSorters )
                              }
                          };
                      }
                      else {
                          data.properties = {
                              [ sortersPropertyTypeIdString ]: {
                                  statusId: metaModel[ 'CFG' ].getStatus( 'deleted' )?.id
                              }
                          };
                      }

                      dispatch( updateItem(
                          configDataContext,
                          chartItem.id,
                          data,
                          additionalElusiveDataContexts
                      ) );
                  }

                  isColumnClicked.current = true;
              },
              [
                  additionalElusiveDataContexts,
                  chartItem,
                  configDataContext
              ]
          ),
          onFilterChange                                             = useCallback(
              ( key : string, field : string, matchMode : FilterMatchMode, value : any, valueType : FilterValueType ) => {
                  const rawColumnsToUse = lastParams.current?.rawColumns as Column[],
                        rawColumn       = rawColumnsToUse.find( rC => rC.dataDescriptor?.alias === field );

                  if ( rawColumn ) {
                      const activeStatusId                            = metaModel[ 'CFG' ].getStatus( 'active' )?.id,
                            deletedStatusId                           = metaModel[ 'CFG' ].getStatus( 'deleted' )?.id,
                            filterOperatorPropertyTypeIdString        = String( metaModel[ 'CFG' ].getPropertyType( 'FilterOperator' )?.id ?? 0 ),
                            filterValuePropertyTypeIdString           = String( metaModel[ 'CFG' ].getPropertyType( 'FilterValue' )?.id ?? 0 ),
                            columnsDataToSet : ItemAttributesSettable = {
                                properties: {}
                            },
                            columnItem                                = global.data[ configDataContext ].items[ rawColumn.data.id ];

                      if ( columnItem ) {
                          if ( columnItem.properties.has( Number( filterOperatorPropertyTypeIdString ) ) ) {
                              columnsDataToSet.properties![ filterOperatorPropertyTypeIdString ] = {
                                  statusId: deletedStatusId
                              };
                          }

                          if ( columnItem.properties.has( Number( filterValuePropertyTypeIdString ) ) ) {
                              columnsDataToSet.properties![ filterValuePropertyTypeIdString ] = {
                                  statusId: deletedStatusId
                              };
                          }
                      }

                      if ( value !== '' ) {
                          columnsDataToSet.properties![ filterOperatorPropertyTypeIdString ] = {
                              value   : matchMode,
                              statusId: activeStatusId
                          };

                          if ( matchMode !== 'empty' && matchMode !== 'notEmpty' ) {
                              columnsDataToSet.properties![ filterValuePropertyTypeIdString ] = {
                                  value   : value,
                                  statusId: activeStatusId
                              };
                          }
                      }

                      dispatch( updateItem(
                          configDataContext,
                          rawColumn.data.id,
                          columnsDataToSet,
                          additionalElusiveDataContexts
                      ) );
                  }
              },
              [
                  additionalElusiveDataContexts,
                  configDataContext
              ]
          ),
          onFilterEnterKey                                           = useCallback(
              () => {
                  // do nothing
              },
              [
                  'onlyOnce'
              ]
          ),
          onReload                                                   = useCallback(
              () => {
                  return true;
              },
              [
                  'onlyOnce'
              ]
          ),
          buildDataStructure                                         = () : DataStructure | undefined => {
              let retVal : DataStructure | undefined;

              if ( query ) {
                  if ( query.type === 'DataStructure' ) {
                      retVal = query.dataStructure;
                  }
                  else {
                      retVal = getDataStructureForSqlQuery();
                  }
              }

              return retVal;
          },
          buildXAxisLabelDataDescriptorAndDataSetsAndDataDescriptors = ( dataStructureToUse = dataStructure, columnDataDescriptors = elusiveData?.columnDataDescriptors ) => {
              const retVal : {
                  xAxisLabelDataDescriptor? : DataDescriptor;
                  dataSets? : ChartDataSet[];
                  dataDescriptors? : DataDescriptor[];
              } = {};

              if ( chart && chartItem && dataStructureToUse ) {
                  if ( query?.type === 'DataStructure' ) {
                      retVal.xAxisLabelDataDescriptor = chart.xAxisLabelDataDescriptor;
                      retVal.dataSets = dataExtraction.extractChartDataSets( chartItem, configDataContext, dataStructureToUse );
                  }
                  else if ( columnDataDescriptors ) {
                      retVal.xAxisLabelDataDescriptor = getXAxisLabelDescriptor( chartItem, columnDataDescriptors, mode === 'design' );
                      retVal.dataSets = getDataSets( columnDataDescriptors, chartItem, configDataContext, mode === 'design' );
                  }
              }

              // generate demo data if
              if ( showDemoData ) {
                  if ( !retVal.xAxisLabelDataDescriptor ) {
                      if ( dataStructureToUse ) {
                          if ( query?.type === 'DataStructure' ) {
                              retVal.xAxisLabelDataDescriptor = buildDataDescriptor(
                                  {
                                      attributePath: 'item.key',
                                      valueType    : 'STRING'
                                  },
                                  dataStructureToUse
                              );
                          }
                          else if ( columnDataDescriptors?.length ) {
                              retVal.xAxisLabelDataDescriptor = columnDataDescriptors[ 0 ];
                          }
                      }

                      if ( !retVal.xAxisLabelDataDescriptor ) {
                          retVal.xAxisLabelDataDescriptor = buildDataDescriptor(
                              {
                                  queryFieldName: 'xAxis',
                                  valueType     : 'STRING'
                              },
                              dataStructureToUse ?? getDataStructureForSqlQuery()
                          );
                      }
                  }

                  if ( !retVal.dataSets?.length ) {
                      retVal.dataSets = [];

                      if ( dataStructureToUse ) {
                          if ( query?.type === 'DataStructure' ) {
                              const itemType = metaModel[ dataSchema ].getItemType( dataStructureToUse.item.objectType ?? '' );

                              if ( itemType ) {
                                  for ( const propertySet of itemType.propertySets ) {
                                      if ( propertySet.valueType === 'DOUBLE' || propertySet.valueType === 'INTEGER' ) {
                                          const dataSetDataDescriptor = buildDataDescriptor(
                                              {
                                                  attributePath: `property.${propertySet.propertyType.name}.value`
                                              },
                                              dataStructureToUse
                                          );

                                          retVal.dataSets.push( {
                                              data           : {
                                                  id                   : -1,
                                                  key                  : 'generatedDemoDataSet',
                                                  name                 : 'generatedDemoDataSet',
                                                  order                : 10,
                                                  queryFieldName       : 'yAxis',
                                                  convertBarsToDataSets: false
                                              },
                                              dataDescriptor : dataSetDataDescriptor,
                                              dataDescriptors: [ dataSetDataDescriptor! ]
                                          } );
                                          break;
                                      }
                                  }
                              }
                          }
                          else if ( columnDataDescriptors?.length ) {
                              for ( let i = 1; i < columnDataDescriptors.length; i++ ) {
                                  retVal.dataSets.push( {
                                      data           : {
                                          id                   : -i,
                                          key                  : `generatedDemoDataSet_${i}`,
                                          name                 : `generatedDemoDataSet_${i}`,
                                          order                : i * 10,
                                          queryFieldName       : `yAxis_${i}`,
                                          convertBarsToDataSets: false
                                      },
                                      dataDescriptor : columnDataDescriptors[ i ],
                                      dataDescriptors: [ columnDataDescriptors[ i ] ]
                                  } );
                              }
                          }
                      }

                      if ( !retVal.dataSets.length ) {
                          const dataSetDatadescriptor = buildDataDescriptor(
                              {
                                  queryFieldName: 'yAxis',
                                  valueType     : 'INTEGER'
                              },
                              dataStructureToUse ?? getDataStructureForSqlQuery()
                          );

                          retVal.dataSets.push( {
                              data           : {
                                  id                   : -1,
                                  key                  : 'generatedDemoDataSet',
                                  name                 : 'generatedDemoDataSet',
                                  order                : 10,
                                  queryFieldName       : 'yAxis',
                                  convertBarsToDataSets: false
                              },
                              dataDescriptor : dataSetDatadescriptor,
                              dataDescriptors: [ dataSetDatadescriptor! ]
                          } );
                      }
                  }
              }

              retVal.dataDescriptors = [];

              if ( retVal.xAxisLabelDataDescriptor ) {
                  retVal.dataDescriptors.push( retVal.xAxisLabelDataDescriptor );
              }

              if ( retVal.dataSets ) {
                  for ( const dataSet of retVal.dataSets ) {
                      if ( dataSet.dataDescriptors?.length ) {
                          for ( const dataDescriptor of dataSet.dataDescriptors ) {
                              if ( !retVal.dataDescriptors.find( dD => dD.alias === dataDescriptor.alias ) ) {
                                  retVal.dataDescriptors.push( dataDescriptor );
                              }
                          }
                      }
                  }
              }

              return retVal;
          },
          buildRawColumns                                            = ( queryToUse = query, xAxisLabelDataDescriptorToUse = xAxisLabelDataDescriptor, dataSetsToUse = dataSets ) => {
              const retVal : Column[] = [];

              if ( chartItem && queryToUse && dataSetsToUse ) {
                  if ( xAxisLabelDataDescriptorToUse ) {
                      // time values column
                      if (
                          queryToUse.type === 'DataStructure'
                          && chart?.data?.timeSeriesIntervalUnit
                          && ( chart.data.timeSeriesIntervalEndAbsolute ?? chart.data.timeSeriesIntervalEndRelative )
                          && ( chart.data.timeSeriesIntervalStartAbsolute ?? chart.data.timeSeriesIntervalStartRelative )
                      ) {
                          retVal.push( {
                              data          : {
                                  id        : 0,
                                  key       : 'genericTimeStamp',
                                  name      : 'genericTimeStamp',
                                  isEditable: false,
                                  isHidden  : false,
                                  valueType : 'DATE',
                                  order     : 0
                              },
                              dataDescriptor: buildDataDescriptor( { queryFieldName: 'x' }, getDataStructureForSqlQuery() ),
                              messages      : {
                                  header: __( 'YN.base.frontend.chartDesigner.column.timestamp.title' )
                              },
                              format        : {
                                  type: 'Custom',
                                  fnc : ( inValue : string ) => {
                                      let outValue : string = inValue;

                                      switch ( chart.data.timeSeriesIntervalUnit ) {
                                          case 'millisecond':
                                              outValue = moment( Number( inValue ) ).format( __( 'YN.base.common.ChartJS.time.displayFormats.millisecond' ) );
                                              break;

                                          case 'second'     :
                                              outValue = moment( Number( inValue ) ).format( __( 'YN.base.common.ChartJS.time.displayFormats.second' ) );
                                              break;

                                          case 'minute'     :
                                              outValue = moment( Number( inValue ) ).format( __( 'YN.base.common.ChartJS.time.displayFormats.minute' ) );
                                              break;

                                          case  'hour':
                                              outValue = moment( Number( inValue ) ).format( __( 'YN.base.common.ChartJS.time.displayFormats.hour' ) );
                                              break;

                                          case 'day'        :
                                              outValue = moment( Number( inValue ) ).format( __( 'YN.base.common.ChartJS.time.displayFormats.day' ) );
                                              break;

                                          case 'month'      :
                                              outValue = moment( Number( inValue ) ).format( __( 'YN.base.common.ChartJS.time.displayFormats.month' ) );
                                              break;

                                          case 'quarter'    :
                                              outValue = moment( Number( inValue ) ).format( __( 'YN.base.common.ChartJS.time.displayFormats.quarter' ) );
                                              break;

                                          case  'year':
                                              outValue = moment( Number( inValue ) ).format( __( 'YN.base.common.ChartJS.time.displayFormats.year' ) );
                                              break;
                                      }

                                      return outValue;
                                  }
                              }
                          } );
                      }

                      let label : string = xAxisLabelDataDescriptorToUse.field;

                      if ( chart?.messages?.xAxisLabel ) {
                          label = __( typeof chart?.messages?.xAxisLabel === 'string' ? chart?.messages?.xAxisLabel : chart?.messages?.xAxisLabel.key );
                      }
                      else if ( queryToUse.type === 'DataStructure' ) {
                          label = __( dataUtils.getMetaModelLabelMsgId( xAxisLabelDataDescriptorToUse, queryToUse.dataStructure, dataSchema ) )

                          // get ItemType label if more than one DataObject
                          if ( xAxisLabelDataDescriptorToUse?.dataObject?.item.objectType && extractDataObjectsFromDataStructure( queryToUse.dataStructure ).length > 1 ) {
                              const itemTypeLabel = metaModel[ schema ].getItemType( xAxisLabelDataDescriptorToUse.dataObject.item.objectType )?.label;

                              if ( itemTypeLabel ) {
                                  label = `${itemTypeLabel} ${label}`;
                              }
                          }
                      }

                      // x-axis values column
                      retVal.push( {
                          data          : {
                              id        : chartItem.id,
                              key       : xAxisLabelDataDescriptorToUse.alias,
                              name      : xAxisLabelDataDescriptorToUse.alias,
                              isEditable: false,
                              isHidden  : false,
                              valueType : xAxisLabelDataDescriptorToUse.valueType ?? 'STRING',
                              order     : 1
                          },
                          dataDescriptor: xAxisLabelDataDescriptorToUse,
                          messages      : {
                              header: __( 'YN.base.frontend.chartDesigner.column.label.title', { field: label } )
                          }
                      } );
                  }

                  for ( let dataSetIndex = 0; dataSetIndex < dataSetsToUse.length; dataSetIndex++ ) {
                      const rawColumn = chartUtilities.getRawColumn( dataSetsToUse[ dataSetIndex ], queryToUse, dataSchema );

                      if ( rawColumn ) {
                          retVal.push( rawColumn );
                      }
                      /*const dataSet = dataSetsToUse[ dataSetIndex ];

                      if ( dataSet.dataDescriptor ) {
                          let label : string;

                          if ( dataSet.messages?.label ) {
                              label = __( typeof dataSet.messages.label === 'string' ? dataSet.messages.label : dataSet.messages.label.key );
                          }
                          else {
                              if ( queryToUse.type === 'DataStructure' ) {
                                  label = __( dataUtils.getMetaModelLabelMsgId( dataSet.dataDescriptor, queryToUse.dataStructure, dataSchema ) );
                              }
                              else {
                                  label = dataSet.dataDescriptor.field;
                              }

                              if ( !label ) {
                                  label = dataSet.data.name;
                              }
                          }

                          retVal.push( {
                              data          : {
                                  id       : dataSet.data.id,
                                  key      : dataSet.data.key,
                                  name     : dataSet.data.name,
                                  valueType: dataSet.dataDescriptor.valueType ?? 'STRING',
                                  order    : dataSet.data.order ?? ( dataSetIndex + 1 ) * 10
                              },
                              dataDescriptor: dataSet.dataDescriptor,
                              messages      : {
                                  header: __( 'YN.base.frontend.chartDesigner.column.dataSet.title', { field: label } )
                              }
                          } );
                      }*/
                  }

                  // order columns
                  retVal.sort( ( column1, column2 ) => {
                      let sortRetVal = 0;

                      if ( column1.data.order && column2.data.order ) {
                          if ( column1.data.order > column2.data.order ) {
                              sortRetVal = 1;
                          }
                          else if ( column1.data.order < column2.data.order ) {
                              sortRetVal = -1;
                          }
                      }

                      return sortRetVal;
                  } );
              }

              return retVal;
          },
          buildColumnDefinitions                                     = ( rawColumnsToUse = rawColumns, queryToUse = query ) => {
              const retVal : ColumnDefinition[] = [];

              if ( rawColumnsToUse && queryToUse ) {
                  for ( const rawColumn of rawColumnsToUse ) {
                      retVal.push( {
                          layoutElementId: rawColumn.data.id,
                          ...chartUtilities.getColumnDefinition(
                              rawColumn.dataDescriptor!,
                              rawColumn.messages?.header as string,
                              queryToUse.dataStructure,
                              dataSchema
                          ),
                          format: rawColumn.format
                      } );
                  }
              }

              return retVal;
          },
          buildColumns                                               = ( columnDefinitionsToUse = columnDefinitions ) => {
              const retVal : ColumnConfig[] = [];

              if ( columnDefinitionsToUse ) {
                  for ( let columnIndex = 0; columnIndex < columnDefinitionsToUse.length; columnIndex++ ) {
                      const columnDefinition = columnDefinitionsToUse[ columnIndex ],
                            realColumn       = !columnDefinition.dataDescriptor?.fullAlias.match( /^createdDataDescriptor::/ );

                      if ( mode === 'design' || realColumn ) {
                          const column : ColumnConfig = {
                              key        : columnDefinition.key,
                              field      : columnDefinition.field,
                              width      : columnDefinition.width,
                              sortable   : realColumn && columnDefinition.dataDescriptor?.field !== 'x',
                              reorderable: realColumn && columnDefinition.dataDescriptor?.field !== 'x' && Boolean( columnIndex > ( isTimeSeriesChart ? 1 : 0 ) )
                          };

                          if ( mode === 'design' ) {
                              if ( columnDefinition.dataDescriptor?.field === 'x' ) {
                                  column.pt = {
                                      headerCell: {
                                          id                            : `leid_${chartItem.id}_timestamp`,
                                          className                     : 'yn-no-delete yn-chartdesigner-multicolumn-left',
                                          [ 'data-layoutelementid' ]    : String( chartItem.id ),
                                          [ 'data-datadescriptoralias' ]: '',
                                          draggable                     : false,
                                          style                         : {
                                              [ '--column-height' ]       : `${dataTableHeight}px`,
                                              [ '--column-header-height' ]: `${dataTableHeaderHeight}px`
                                          },
                                          onMouseOver                   : onMouseEnter.bind( null, chartItem.id ),
                                          onMouseOutCapture             : onMouseLeave.bind( null, chartItem.id )
                                      }
                                  };
                              }
                              else {
                                  const className : string[] = [];

                                  if ( !realColumn ) {
                                      className.push( 'yn-disabled' );
                                      className.push( 'yn-error' );
                                  }

                                  if ( columnIndex <= ( isTimeSeriesChart ? 1 : 0 ) ) {
                                      className.push( 'yn-no-delete' );

                                      if ( isTimeSeriesChart ) {
                                          className.push( 'yn-chartdesigner-multicolumn-right' );
                                      }
                                  }

                                  if ( readOnly ) {
                                      className.push( 'yn-disabled' );
                                      className.push( 'yn-no-delete' );
                                  }

                                  column.pt = {
                                      headerCell   : {
                                          id                            : `leid_${columnDefinition.layoutElementId}`,
                                          className                     : className.join( ' ' ),
                                          [ 'data-layoutelementid' ]    : String( columnDefinition.layoutElementId ),
                                          [ 'data-datadescriptoralias' ]: `${columnDefinition.dataDescriptor?.alias ? `${columnDefinition.dataDescriptor.alias} ` : ''}${columnDefinition.dataDescriptor?.fullAlias.split( ',' ).join( ' ' ) ?? ''}`,
                                          draggable                     : realColumn && columnIndex > ( isTimeSeriesChart ? 1 : 0 ) && !readOnly,
                                          style                         : {
                                              [ '--column-height' ]       : `${dataTableHeight}px`,
                                              [ '--column-header-height' ]: `${dataTableHeaderHeight}px`
                                          },
                                          onMouseOver                   : onMouseEnter.bind( null, columnDefinition.layoutElementId ),
                                          onMouseOutCapture             : onMouseLeave.bind( null, columnDefinition.layoutElementId ),
                                          onClickCapture                : onClick.bind( null, columnDefinition.layoutElementId ),
                                          onDragStart                   : realColumn && columnIndex > ( isTimeSeriesChart ? 1 : 0 ) && !readOnly
                                              ? onDragStart.bind( null, columnDefinition.layoutElementId )
                                              : undefined
                                      },
                                      columnResizer: {
                                          onClickCapture    : onClick.bind( null, columnDefinition.layoutElementId ),
                                          onMouseDownCapture: onColumnResizeStart.bind( null, columnDefinition.layoutElementId )
                                      }
                                  };
                              }
                          }

                          retVal.push( column );
                      }
                  }
              }

              return retVal;
          },
          loadColumnDataDescriptors                                  = async () => {
              const innerStartTime = debug ? performance.now() : 0;

              try {
                  if ( !isLoading.current && ( query?.type === 'SQL' || query?.type === 'Cypher' ) ) {
                      const params : LoadColumnDataDescriptorsParameters = {
                          type      : query.type,
                          schema    : query.schema,
                          query     : query.query,
                          parameters: query.parameters
                      };

                      if ( elusiveData?.lastColumnDataDescriptorParameters !== JSON.stringify( params ) ) {
                          if ( debug ) {
                              console.groupCollapsed( `%c${'%cOverview - %cloadColumnDataDescriptors%c'.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.loadFunction};`, 'font-weight: normal;' );
                          }

                          isLoading.current = true;

                          dispatch( startRequest(
                              () => {
                              },
                              uid,
                              'processing'
                          ) );

                          await dispatch( loadData(
                              'chart/loadColumnDataDescriptors',
                              configDataContext,
                              configDataContext,
                              'loadColumnDataDescriptors',
                              'CFG',
                              {
                                  params
                              },
                              false,
                              ( result ) => {
                                  const {
                                            xAxisLabelDataDescriptor: newXAxisLabelDataDescriptor,
                                            dataSets                : newDataSets,
                                            dataDescriptors         : newDataDescriptors
                                        } = buildXAxisLabelDataDescriptorAndDataSetsAndDataDescriptors( dataStructure, result.data.columnDataDescriptors );

                                  dispatch( setElusiveData( uid, {
                                      lastColumnDataDescriptorParameters: JSON.stringify( params ),
                                      columnDataDescriptors             : result.data.columnDataDescriptors,
                                      xAxisLabelDataDescriptor          : newXAxisLabelDataDescriptor,
                                      dataSets                          : newDataSets,
                                      dataDescriptors                   : newDataDescriptors
                                  } ) );
                              }
                          ) );

                          dispatch( endRequest(
                              () => {
                              },
                              uid,
                              'processing'
                          ) );

                          isLoading.current = false;

                          if ( debug ) {
                              console.log( `Form - %cloadColumnDataDescriptors end - ${Math.round( 1000 * ( performance.now() - innerStartTime ) ) / 1000} ms%c`.asLogLine( 106 ), `color: #${debugColorScheme.loadFunction}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
                              console.groupEnd();
                          }
                      }
                  }
              }
              catch ( e ) {
                  console.error( e );
              }
          },
          startDrag                                                  = ( rootLayoutElementId : number, data : DragData, options : DragOptions, e : React.DragEvent<HTMLElement> ) : void => {
              if ( dataStructure && query ) {
                  const configDataContextForExtract = 'temp_chartDesigner_startDrag',
                        rootItem                    = data.items[ rootLayoutElementId ];

                  global.data[ configDataContextForExtract ] = {
                      items     : {
                          ...data.items
                      },
                      references: {
                          ...data.references
                      },
                      schema    : 'CFG'
                  };

                  const dataSet = dataExtraction.extractChartDataSetData( rootItem, configDataContextForExtract, dataStructure );

                  if ( dataSet ) {
                      if ( elusiveData?.columnDataDescriptors?.length ) {
                          processDataSet( dataSet, elusiveData?.columnDataDescriptors, true );
                      }

                      const rawColumn = chartUtilities.getRawColumn( dataSet, query, dataSchema );

                      if ( rawColumn ) {
                          newDragInfo.current = {
                              data,
                              rawColumn
                          };

                          onDragStart( rootLayoutElementId, e, options );

                          return;
                      }
                  }
              }

              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.preventDefault();
          },
          markLayoutElementsForAlias                                 = ( alias : string | string[] | undefined ) : void => {
              if ( typeof alias === 'string' ) {
                  alias = [ alias ];
              }

              setAliasToMark( alias );
          },
          areArraysEqual                                             = ( arr1 : any[] | undefined, arr2 : any[] | undefined ) => {
              let retVal : boolean = false;

              if ( !arr1?.length && !arr2?.length ) {
                  retVal = true;
              }
              else if ( arr1 && arr2 && JSON.stringify( arr1 ) === JSON.stringify( arr2 ) ) {
                  retVal = true;
              }

              return retVal;
          },
          chartElement                                               = useMemo(
              () => {
                  return hasMounted.current
                      ? <ChartComponent
                          key={`${uid}_${chartUidExtension}`}

                          uid={`${uid}_${chartUidExtension}`}

                          dataContext={dataContext}

                          viewKey="designer"
                          limitToHeight={true}
                          type={chart?.data?.type}
                          dataDescriptors={dataDescriptors}
                          dataSets={dataSets}
                          dataStructure={dataStructure}
                          queryType={query?.type}
                          scales={scales}
                          schema={dataSchema}
                          legendAlign={chart?.data?.legendAlign}
                          legendPosition={chart?.data?.legendPosition}
                          timeSeriesIntervalUnit={chart?.data?.timeSeriesIntervalUnit}
                          xAxisLabelDataDescriptor={chart?.xAxisLabelDataDescriptor}
                      />
                      : <></>;
              },
              [
                  chart?.data?.legendAlign,
                  chart?.data?.legendPosition,
                  chart?.data?.timeSeriesIntervalUnit,
                  chart?.data?.type,
                  chart?.xAxisLabelDataDescriptor,
                  chartUidExtension,
                  dataDescriptors,
                  dataSchema,
                  dataSets,
                  dataStructure,
                  hasMounted.current,
                  mode,
                  query?.type,
                  scales,
                  uid
              ]
          ),
          dataTableValue                                             = useMemo(
              () => {
                  const dataTableProps = {
                            ...dataTableComponentDefaultProps,
                            ...dataTableComponentProps
                        },
                        {
                            page,
                            rows
                        }              = dataTableProps;

                  let retVal : any[] = [];

                  if ( elusiveData?.metaData?.rowData ) {
                      retVal = elusiveData.metaData.rowData.slice( ( page - 1 ) * rows, page * rows );
                  }

                  return retVal;
              },
              [
                  elusiveData?.metaData?.rowData,
                  dataTableComponentProps?.page,
                  dataTableComponentProps?.rows
              ]
          ),
          dataTableElement                                           = useMemo(
              () => {
                  return hasMounted.current
                      ? <DataTableComponent
                          {...dataTableComponentProps}

                          key={`${uid}_${dataTableUidExtension}`}

                          ref={dataTable}

                          uid={`${uid}_${dataTableUidExtension}`}

                          dataContext={dataContext}

                          dataStructure={dataStructure}
                          columnDefinitions={columnDefinitions}

                          //forceFit={dataTableComponentProps?.forceFit ?? true}
                          autoLayout={dataTableComponentProps?.autoLayout ?? true}

                          columns={dataTableComponentProps?.columns ?? columns}

                          filterDisplay="menu"
                          filters={filters}
                          onFilterChange={mode === 'design' ? onFilterChange : undefined}
                          onFilterEnterKey={mode === 'design' ? onFilterEnterKey : undefined}

                          sorters={sorters}
                          onSort={mode === 'design' ? onSort : undefined}

                          value={dataTableValue}
                          valueFieldMapping={elusiveData?.metaData?.rowDataMapping ?? {}}
                          totalRecords={elusiveData?.metaData?.total ?? 0}

                          reload={onReload}
                          className="yn-datatable-full yn-flex yn-datatable-border"

                          selectable={false}
                      />
                      : <></>;
              },
              [
                  columnDefinitions,
                  columns,
                  dataContext,
                  dataStructure,
                  dataTableComponentProps,
                  dataTableUidExtension,
                  dataTableValue,
                  elusiveData?.metaData?.rowDataMapping,
                  elusiveData?.metaData?.total,
                  filters,
                  hasMounted.current,
                  mode,
                  onFilterChange,
                  onFilterEnterKey,
                  onReload,
                  onSort,
                  sorters,
                  uid
              ]
          );

    // expose methods via reference
    useImperativeHandle(
        forwardedRef,
        () => ( {
            chart,
            dataStructure,
            columns: rawColumns,
            columnDefinitions,
            dataSets,
            markLayoutElementsForAlias,
            scales,
            startDrag
        } )
    );

    // init (on mount / unmount)
    useEffect(
        () => {
            hasMounted.current = true;

            dispatch( setElusiveDataForChart( `${uid}_${chartUidExtension}`, {
                configLoaded: true,
                dataLoaded  : true
            } ) );

            return () => {
                if ( timeoutId.current ) {
                    clearTimeout( timeoutId.current );
                }
            };
        },
        []
    );

    // compute dataTable height (on update)
    useLayoutEffect(
        () => {
            const dataTableDivElement       = document.querySelector( '.yn-chart-designer .p-datatable .p-datatable-wrapper' ) as HTMLDivElement,
                  dataTableHeaderDivElement = document.querySelector( '.yn-chart-designer .p-datatable .p-datatable-wrapper .p-datatable-thead' ) as HTMLDivElement;

            if (
                dataTableComponentProps?.autoLayout !== undefined
                && dataTableDivElement?.clientHeight
                && dataTableHeight !== dataTableDivElement.clientHeight
            ) {
                setDataTableHeight( dataTableDivElement.clientHeight );
            }

            if (
                dataTableHeaderDivElement?.clientHeight
                && dataTableHeaderHeight !== dataTableHeaderDivElement.clientHeight
            ) {
                setDataTableHeaderHeight( dataTableHeaderDivElement.clientHeight );
            }
        }
    );

    // compute internal data / load data (on update)
    useEffect(
        () => {
            if ( !isLoading.current ) {
                const innerStartTime                 = debug ? performance.now() : 0,
                      recompute                      = elusiveData === undefined,
                      elusiveDataToSet : ElusiveData = {};

                // dataStructure
                if (
                    recompute
                    || globalConfigDataVersions !== lastParams.current?.globalConfigDataVersions
                    || query !== lastParams.current?.query
                ) {
                    const newDataStructure = buildDataStructure();

                    if ( JSON.stringify( newDataStructure ?? {} ) !== JSON.stringify( dataStructure ?? {} ) ) {
                        dataStructure = newDataStructure;
                        elusiveDataToSet.dataStructure = newDataStructure;
                    }
                }

                // build xAxisLabelDescriptor, dataSets and dataDescriptors
                if (
                    recompute
                    || globalConfigDataVersions !== lastParams.current?.globalConfigDataVersions
                    || dataStructure !== lastParams.current?.dataStructure
                    || query !== lastParams.current?.query
                    || mode !== lastParams.current?.mode
                ) {
                    // load columnDataDescriptors for sql or cypher queries
                    if (
                        (
                            query?.type === 'SQL'
                            || query?.type === 'Cypher'
                        )
                        && query.query !== lastParams.current?.query?.query
                    ) {
                        void loadColumnDataDescriptors();
                    }
                    else {
                        const {
                                  xAxisLabelDataDescriptor: newXAxisLabelDataDescriptor,
                                  dataSets                : newDataSets,
                                  dataDescriptors         : newDataDescriptors
                              } = buildXAxisLabelDataDescriptorAndDataSetsAndDataDescriptors( dataStructure, elusiveData?.columnDataDescriptors );

                        if ( JSON.stringify( newXAxisLabelDataDescriptor ?? {} ) !== JSON.stringify( xAxisLabelDataDescriptor ?? {} ) ) {
                            const oldField = xAxisLabelDataDescriptor?.alias;

                            xAxisLabelDataDescriptor = newXAxisLabelDataDescriptor;
                            elusiveDataToSet.xAxisLabelDataDescriptor = newXAxisLabelDataDescriptor;

                            // remove old one from sorters if
                            if ( oldField && sorters?.length ) {
                                const data : ItemAttributesSettable = {},
                                      index                         = sorters.findIndex( sorter => sorter.field === oldField );

                                if ( index !== -1 ) {
                                    const sortersPropertyTypeIdString = String( metaModel[ 'CFG' ].getPropertyType( 'Sorters' )!.id );

                                    sorters.splice( index, 1 );

                                    if ( sorters.length ) {
                                        data.properties = {
                                            [ sortersPropertyTypeIdString ]: {
                                                value: JSON.stringify( sorters )
                                            }
                                        }
                                    }
                                    else {
                                        data.properties = {
                                            [ sortersPropertyTypeIdString ]: {
                                                statusId: metaModel[ 'CFG' ].getStatus( 'deleted' )?.id
                                            }
                                        }
                                    }

                                    dispatch( updateItem(
                                        configDataContext,
                                        chartItem.id,
                                        data,
                                        additionalElusiveDataContexts
                                    ) );
                                }
                            }
                        }
                        if ( JSON.stringify( newDataSets ?? {} ) !== JSON.stringify( dataSets ?? {} ) ) {
                            const oldFields = dataSets?.reduce(
                                      ( innerRetVal, dataSet ) => {
                                          if ( dataSet.dataDescriptor?.alias ) {
                                              innerRetVal.push( dataSet.dataDescriptor.alias );
                                          }

                                          return innerRetVal;
                                      },
                                      [] as string[]
                                  ) ?? [],
                                  newFields = newDataSets?.reduce(
                                      ( innerRetVal, dataSet ) => {
                                          if ( dataSet.dataDescriptor?.alias ) {
                                              innerRetVal.push( dataSet.dataDescriptor.alias );
                                          }

                                          return innerRetVal;
                                      },
                                      [] as string[]
                                  ) ?? [];

                            dataSets = newDataSets;
                            elusiveDataToSet.dataSets = newDataSets;

                            // remove old ones from sorters if
                            if ( oldFields.length && sorters?.length ) {
                                const data : ItemAttributesSettable = {};
                                const sortersPropertyTypeIdString = String( metaModel[ 'CFG' ].getPropertyType( 'Sorters' )!.id );

                                let changed = false;

                                for ( const oldField of oldFields ) {
                                    if ( !newFields.includes( oldField ) ) {
                                        const index = sorters.findIndex( sorter => sorter.field === oldField );

                                        if ( index !== -1 ) {
                                            changed = true;

                                            sorters.splice( index, 1 );
                                        }
                                    }
                                }

                                if ( changed ) {
                                    if ( sorters.length ) {
                                        data.properties = {
                                            [ sortersPropertyTypeIdString ]: {
                                                value: JSON.stringify( sorters )
                                            }
                                        }
                                    }
                                    else {
                                        data.properties = {
                                            [ sortersPropertyTypeIdString ]: {
                                                statusId: metaModel[ 'CFG' ].getStatus( 'deleted' )?.id
                                            }
                                        }
                                    }

                                    dispatch( updateItem(
                                        configDataContext,
                                        chartItem.id,
                                        data,
                                        additionalElusiveDataContexts
                                    ) );
                                }
                            }
                        }
                        if ( JSON.stringify( newDataDescriptors ?? {} ) !== JSON.stringify( dataDescriptors ?? {} ) ) {
                            dataDescriptors = newDataDescriptors;
                            elusiveDataToSet.dataDescriptors = newDataDescriptors;
                        }
                    }
                }

                // stop computing on loading
                if ( !isLoading.current ) {

                    // build rawColumns
                    if (
                        recompute
                        || globalConfigDataVersions !== lastParams.current?.globalConfigDataVersions
                        || xAxisLabelDataDescriptor !== lastParams.current?.xAxisLabelDataDescriptor
                        || dataSets !== lastParams.current?.dataSets
                        || query !== lastParams.current?.query
                    ) {
                        const newRawColumns = buildRawColumns( query, xAxisLabelDataDescriptor, dataSets );

                        if ( JSON.stringify( newRawColumns ?? {} ) !== JSON.stringify( rawColumns ?? {} ) ) {
                            rawColumns = newRawColumns;
                            elusiveDataToSet.rawColumns = newRawColumns;
                        }
                    }

                    // build columnDefinitions
                    if (
                        recompute
                        || rawColumns !== lastParams.current?.rawColumns
                        || query !== lastParams.current?.query
                    ) {
                        const newColumnDefinitions = buildColumnDefinitions( rawColumns, query );

                        if ( JSON.stringify( newColumnDefinitions ?? {} ) !== JSON.stringify( columnDefinitions ?? {} ) ) {
                            columnDefinitions = newColumnDefinitions;
                            elusiveDataToSet.columnDefinitions = newColumnDefinitions;
                        }
                    }

                    // build columns
                    if (
                        recompute
                        || columnDefinitions !== lastParams.current?.columnDefinitions
                        || dataTableHeaderHeight !== lastParams.current?.dataTableHeaderHeight
                        || dataTableHeight !== lastParams.current?.dataTableHeight
                        || mode !== lastParams.current?.mode
                        || isTimeSeriesChart !== lastParams.current?.isTimeSeriesChart
                    ) {
                        const newColumns = buildColumns( columnDefinitions );

                        if ( JSON.stringify( newColumns ?? [] ) !== JSON.stringify( columns ?? [] ) ) {
                            columns = newColumns;
                            elusiveDataToSet.columns = newColumns;

                            // clear dataTable columns
                            dataTable.current?.dataTable?.resetResizeColumnsWidth();
                            dispatch( setParamsForDataTable( `${uid}_${dataTableUidExtension}`, {
                                columns   : undefined,
                                forceFit  : undefined,
                                autoLayout: undefined
                            } ) );
                        }
                    }

                    // load data
                    if (
                        (
                            showData
                            || showDemoData
                        )
                        && (
                            !isDataLoaded
                            || !areArraysEqual( columns?.map( c => c.field ), lastParams.current?.columns?.map( ( c : ColumnConfig ) => c.field ) )
                            || !areArraysEqual( filters, lastParams.current?.filters )
                            || !areArraysEqual( sorters, lastParams.current?.sorters )
                            || ( showData !== lastParams.current?.showData )
                            || ( showDemoData !== lastParams.current?.showDemoData )
                            || ( !showData && showDemoData && chart?.data.type !== lastParams.current?.chart?.data.type )
                        )
                    ) {
                        elusiveDataToSet.metaData = undefined;

                        dispatch( setElusiveDataForChart( `${uid}_${chartUidExtension}`, {
                            metaData: {
                                ...chartElusiveData?.metaData,
                                rowData       : undefined,
                                rowDataMapping: undefined
                            }
                        } ) );

                        void load( filters ?? [], sorters ?? [] );
                    }
                    else if ( !showData && !showDemoData && isDataLoaded ) {
                        elusiveDataToSet.isDataLoaded = false;
                        elusiveDataToSet.metaData = undefined;

                        dispatch( setElusiveDataForChart( `${uid}_${chartUidExtension}`, {
                            metaData: {
                                ...chartElusiveData?.metaData,
                                rowData       : undefined,
                                rowDataMapping: undefined
                            }
                        } ) );
                    }
                }

                if ( Object.keys( elusiveDataToSet ).length ) {
                    dispatch( setElusiveData( uid, elusiveDataToSet ) );

                    if ( debug ) {
                        additionalRenderTime.current = innerStartTime - performance.now();
                    }
                }

                lastParams.current = {
                    chart,
                    chartItem,
                    columnDefinitions,
                    columns,
                    configDataContext,
                    configRootItemId,
                    dataDescriptors,
                    dataSets,
                    dataStructure,
                    dataTableHeaderHeight,
                    dataTableHeight,
                    filters,
                    globalConfigDataVersions,
                    hoverLayoutElementId,
                    isTimeSeriesChart,
                    messageCatalogTimestamp,
                    metaModelTimestamp,
                    mode,
                    onMarkFieldsForAlias,
                    onSelectLayoutElement,
                    query,
                    rawColumns,
                    selectedLayoutElementId,
                    showData,
                    showDemoData,
                    sorters,
                    uid,
                    user,
                    xAxisLabelDataDescriptor
                };
            }
        }
    );

    // mark/unmark dragged layoutElement (on update)
    useLayoutEffect(
        () => {
            // remove current
            const elements : HTMLCollectionOf<Element> | null | undefined = document.getElementsByClassName( 'yn-chartdesigner-dragged' );

            let element : Element | null | undefined;

            if ( elements?.length ) {
                while ( elements[ 0 ] ) {
                    elements[ 0 ].classList.remove( 'yn-chartdesigner-dragged' );
                }
            }

            // set current
            if ( dragInfo.current?.dragLayoutElementId ) {
                element = document.getElementById( `leid_${dragInfo.current.dragLayoutElementId}` );

                if ( element ) {
                    element.classList.add( 'yn-chartdesigner-dragged' );
                }
            }
        }
    );

    // mark/unmark selected layoutElement (onUpdate)
    useLayoutEffect(
        () => {
            // remove current
            const elements : HTMLCollectionOf<Element> | null | undefined = document.getElementsByClassName( 'yn-chartdesigner-selected' );

            let element : Element | null | undefined;

            if ( elements?.length ) {
                while ( elements[ 0 ] ) {
                    elements[ 0 ].classList.remove( 'yn-chartdesigner-selected' );
                }
            }

            // set current
            if ( selectedLayoutElementId ) {
                element = document.getElementById( `leid_${selectedLayoutElementId}` );

                if ( element ) {
                    element.classList.add( 'yn-chartdesigner-selected' );

                    if ( isTimeSeriesChart && selectedLayoutElementId === chartItem.id ) {
                        element = document.getElementById( `leid_${selectedLayoutElementId}_timestamp` );

                        if ( element ) {
                            element.classList.add( 'yn-chartdesigner-selected' );
                        }
                    }
                }
            }
        }
    );

    // mark/unmark dirty or erroneous layoutElements (onUpdate)
    useLayoutEffect(
        () => {
            // remove current
            let elements : HTMLCollectionOf<Element> | null | undefined = document.getElementsByClassName( 'yn-chartdesigner-dirty' ),
                element : Element | null | undefined;

            if ( elements?.length ) {
                while ( elements[ 0 ] ) {
                    elements[ 0 ].classList.remove( 'yn-chartdesigner-dirty' );
                }
            }

            elements = document.getElementsByClassName( 'yn-chartdesigner-error' );

            if ( elements?.length ) {
                while ( elements[ 0 ] ) {
                    elements[ 0 ].classList.remove( 'yn-chartdesigner-error' );
                }
            }

            for ( const layoutElementId of Object.keys( itemFormStates ) ) {
                element = document.getElementById( `leid_${layoutElementId}` );

                if ( element ) {
                    element.classList.add( `yn-chartdesigner-${itemFormStates[ layoutElementId ] === 'ERROR' ? 'error' : 'dirty'}` );
                }
            }
        }
    );

    // mark/unmark aliasToMark
    useLayoutEffect(
        () => {
            // remove current
            let elements : HTMLCollectionOf<Element> | NodeListOf<Element> | null | undefined = document.querySelectorAll( '.yn-chartdesigner-using-alias' ),
                index                                                                         = 0;

            if ( elements?.length ) {
                while ( elements[ index ] ) {
                    elements[ index++ ].classList.remove( 'yn-chartdesigner-using-alias' );
                }
            }

            if ( aliasToMark ) {
                const selector : string[] = [];

                for ( const alias of aliasToMark ) {
                    selector.push( `.yn-datatable#${uid}_${dataTableUidExtension}_datatable [data-datadescriptoralias~="${alias}"]` );
                    selector.push( `.yn-datatable#${uid}_${dataTableUidExtension}_datatable [data-datadescriptoralias^="${alias}"]` );
                    selector.push( `.yn-datatable#${uid}_${dataTableUidExtension}_datatable [data-datadescriptoralias$="${alias}"]` );
                }

                elements = document.querySelectorAll( selector.join( ', ' ) );
                index = 0;

                if ( elements?.length ) {
                    while ( elements[ index ] ) {
                        elements[ index++ ].classList.add( 'yn-chartdesigner-using-alias' );
                    }
                }
            }
        },
        [
            aliasToMark
        ]
    );

    // mark/unmark hovered layoutElement
    useLayoutEffect(
        () => {
            // remove current
            const elements : HTMLCollectionOf<Element> | null | undefined = document.getElementsByClassName( 'yn-chartdesigner-hover' );

            let element : Element | null | undefined;

            if ( elements?.length ) {
                while ( elements[ 0 ] ) {
                    elements[ 0 ].classList.remove( 'yn-chartdesigner-hover' );
                }
            }

            // set current
            if ( hoverLayoutElementId ) {
                element = document.getElementById( `leid_${hoverLayoutElementId}` );

                if ( element ) {
                    element.classList.add( 'yn-chartdesigner-hover' );

                    if ( isTimeSeriesChart && hoverLayoutElementId === chartItem.id ) {
                        element = document.getElementById( `leid_${hoverLayoutElementId}_timestamp` );

                        if ( element ) {
                            element.classList.add( 'yn-chartdesigner-hover' );
                        }
                    }
                }
            }
        },
        [
            chartItem?.id,
            columns,
            hoverLayoutElementId,
            isTimeSeriesChart
        ]
    );

    if ( debug ) {
        console.log( `%crender - ${Math.round( 1000 * ( performance.now() - ( startTime + additionalRenderTime.current ) ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.render}; font-weight: bold;`, 'font-weight: unset;' );
        additionalRenderTime.current = 0;
    }

    if ( mode === 'input' ) {
        return <div
            id={`${uid}_chart`}
            className="yn-card-content-panel yn-flex-layout-parent"
            style={{ maxWidth: '120rem', alignSelf: 'center' }}
        >
            <Card
                className="yn-flex-layout-parent"
                style={{ width: '100%', maxWidth: '100%', maxHeight: '100%' }}
            >
                <div
                    className="yn-chart-designer-chart-wrapper"
                >
                    {chartElement}
                </div>
            </Card>
        </div>;
    }
    else {
        /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
        return <div
            id={`${uid}_chart`}
            className="yn-card-content-panel yn-flex-layout-parent yn-chart-designer"
            style={{ flexDirection: 'row' }}
            onClick={onChartClick}
            onMouseLeave={onChartLeave}
        >
            <Card
                className="yn-flex-layout-parent"
                style={{ width: '60%', maxWidth: '60%' }}
            >
                <div
                    className="yn-chart-designer-chart-wrapper"
                >
                    {chartElement}
                </div>
            </Card>
            <Card
                className="yn-flex-layout-parent"
                style={{ width: 'calc(40% - 1rem)', maxWidth: 'calc(40% - 1rem)', marginLeft: '1rem' }}
            >
                {dataTableElement}
            </Card>
        </div>;
    }
} );

ChartDesigner.defaultProps = defaultProps;
ChartDesigner.displayName = 'ChartDesigner';

export default ChartDesigner;
